// Server Order Entry — staff "Take Order" page (admin web portal).
//
// Waiters/servers build an order ticket from the live menu and send it to the
// POS queue via the authenticated endpoint POST /admin/pos/orders. Pricing is
// server-authoritative (the catalog re-prices every line); the on-screen
// subtotal/GST is a preview only, mirroring the storefront cart math.
//
// Additive + gated: this is a NEW page behind POS Queue: Edit. Nothing here
// changes the customer storefront or any existing admin flow.

import React, { useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import AdminNavTabs from '../components/AdminNavTabs';
import { useAdminCapability } from '../components/useAdminCapability';
import { formatMinor, setStoreCurrency } from '../utils/money';
// Scan-to-Pay QR (take-order-scan-to-pay): the order taker can collect NOW by showing a QR
// the customer scans and pays on their phone, terminal-free. Self-contained modal (auth +
// fetch + poll); reused as-is from the component that also serves the queue copy.
import ScanToPayQrModal from '../components/ScanToPayQrModal';
import { canCollectScanToPay } from '../utils/posPayments';
// In-store ticket/token number, read off the create response so the send confirmation can
// tell the waiter the token to call rather than a 12-character UUID.
import { readStoreTicketNumber, hasStoreTicketNumber, orderDisplayNumber } from '../utils/storeTicket';
import {
  configureLanguages,
  getAvailableLanguages,
  isMultilingual,
  setLanguage,
  t,
  useLanguage,
} from '../utils/i18n';
import { LANGUAGE_REGISTRY } from '../utils/i18nRegistry';
import { readDeviceLanguage, writeDeviceLanguage } from '../utils/languageStorage';
import {
  compareCategoryNames,
  localizedCategoryName,
  localizedName,
  matchesSearch,
} from '../utils/menuName';
import {
  groupsOf,
  hasModifiers,
  lineKey,
  pruneSelections,
  seedSelections,
  serializeSelections,
  summarizeSelections,
  toggleSelection,
  unsatisfiedGroupIds,
  validateSelections,
} from '../utils/modifierSelection';
import {
  REQUIRED_FIELD_BY_ORDER_TYPE,
  missingFieldPromptKey,
  missingRequiredField,
} from '../utils/requiredOrderFields';
import {
  PARTY_LABELS,
  dineInTicketsAtTable,
  nextPartyLabelFromTickets,
  tablePartiesEnabled,
  usedPartyLabels,
} from '../utils/partyLabel';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';
const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

// Scope key for the per-device language selection. One admin-portal deployment
// serves exactly one tenant, so this is near-vacuous on web; it exists so the key
// shape matches the mobile app (where tenant switching is real) and so a
// shared-origin deployment could never leak a selection between tenants.
const LANGUAGE_TENANT_KEY = PROJECT_NAME;

// ---------------------------------------------------------------------------
// Pure ticket helpers (exported for unit/property tests — see task 8.6).
//
// A "line" is keyed by `lineId`, which is the product id for a plain add and
// `productId|group:option,...` once customizations are chosen (take-order-modifiers
// decision D2, design Property 8). Identical adds merge and accumulate quantity;
// two "Any Cheese" with different cheeses stay two lines, because they are two
// different things to cook.
//
// `lineKey` produces the bare product id when nothing is selected, so a plain line's
// `lineId` EQUALS its `productId`. That is what lets the quantity/notes helpers key on
// `lineId` without changing any existing caller or breaking a ticket built before this
// feature existed.
// ---------------------------------------------------------------------------

// Authoritative-looking unit price for PREVIEW only. Catalog Price is stored in
// MAJOR units (e.g. "12.50"); convert to integer minor units. Server re-prices.
export function productUnitPriceCents(product) {
  const raw = product == null ? null : (product.Price ?? product.price);
  const n = Number(String(raw == null ? '' : raw).replace(/[$,]/g, '').trim());
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

export function addLine(lines, product, selections) {
  const pid = String(product?.ProductID || '').trim();
  if (!pid) return lines;

  // Only groups the product actually has can contribute to identity or price, so a stale
  // selection map cannot invent a distinct line.
  const groups = groupsOf(product);
  const picked = groups.length ? (pruneSelections(groups, selections) || {}) : {};
  const id = lineKey(pid, picked);

  const existing = lines.find((l) => (l.lineId || l.productId) === id);
  if (existing) {
    return lines.map((l) => ((l.lineId || l.productId) === id ? { ...l, quantity: l.quantity + 1 } : l));
  }

  const translations =
    product && typeof product.ProductNameI18n === 'object' && product.ProductNameI18n !== null
      && !Array.isArray(product.ProductNameI18n)
      ? product.ProductNameI18n
      : null;

  const serialized = serializeSelections(picked);
  const { modifierSummary: summary, modifierPriceCents } = serialized
    ? summarizeSelections(groups, picked)
    : { modifierSummary: '', modifierPriceCents: 0 };
  const baseUnitPriceCents = productUnitPriceCents(product);

  return [
    ...lines,
    {
      lineId: id,
      productId: pid,
      // ENGLISH, always. This is the value submitted to the server, which re-prices
      // from the catalog; a translated name here would break that and every
      // downstream order consumer (R9.6).
      name: String(product?.ProductName || product?.name || 'Item').trim() || 'Item',
      // Display-only copy of the row's translations, carried on the line so the
      // ticket pane can render Tamil without re-looking-up the product. Omitted
      // when absent, so a line from an untranslated menu is shaped exactly as before.
      ...(translations ? { nameI18n: translations } : {}),
      // Includes the modifier PREVIEW, so `subtotalCents` and `gstPreview` need no
      // knowledge of modifiers at all. The server re-derives the real figure and may
      // disagree; it wins, and the ticket is a preview exactly as it always was.
      unitPriceCents: baseUnitPriceCents + modifierPriceCents,
      quantity: 1,
      itemNotes: '',
      // All three omitted for a plain add, so a plain line is shaped byte-identically
      // to pre-feature and the payload builders emit exactly what they emitted before.
      ...(serialized
        ? {
          modifierSelections: serialized,
          modifierSummary: summary,
          modifierPriceCents,
          baseUnitPriceCents,
        }
        : {}),
    },
  ];
}

export function setLineQuantity(lines, lineId, qty) {
  const q = Math.trunc(Number(qty));
  if (!Number.isFinite(q) || q <= 0) {
    return lines.filter((l) => (l.lineId || l.productId) !== lineId);
  }
  return lines.map((l) => ((l.lineId || l.productId) === lineId ? { ...l, quantity: Math.min(q, 99) } : l));
}

export function setLineNotes(lines, lineId, notes) {
  return lines.map((l) => ((l.lineId || l.productId) === lineId
    ? { ...l, itemNotes: String(notes || '').slice(0, 280) }
    : l));
}

/**
 * One line's wire shape, shared by the create and add-items payload builders.
 *
 * Extracted so the two cannot diverge on what a customized line looks like — the same
 * reason `price_item_lines` exists server-side. `modifierSelections` is included only when
 * the line has any, which keeps a plain add's request body byte-identical to pre-feature
 * (design Property 9) and is what selects the server's untouched pricing path.
 *
 * `modifierPriceCents` is deliberately NOT sent. The server derives it from the catalog and
 * ignores anything supplied (decision D4), so sending it would only invite the impression
 * that the client's figure matters.
 */
export function buildLineItem(line) {
  return {
    ProductID: line.productId,
    // English, exactly as the create path sends it (R9.6).
    name: line.name,
    Quantity: line.quantity,
    ...(line.itemNotes ? { item_notes: line.itemNotes } : {}),
    ...(line.modifierSelections ? { modifierSelections: line.modifierSelections } : {}),
  };
}

export function subtotalCents(lines) {
  return (lines || []).reduce((acc, l) => acc + Math.max(0, l.unitPriceCents) * Math.max(0, l.quantity), 0);
}

// GST preview mirrors demo-app PosCartPage taxSummary: single store default rate
// over the whole subtotal, ROUND_HALF_UP, only for an IN_GST store. Returns
// { taxCents, totalCents, inclusive, label } or null (no GST → total = subtotal).
//
// `translate` is injectable and defaults to the real `t`, which is what keeps this
// an exported PURE helper: the existing call sites and unit tests need no change,
// and a test can pass a stub. It also means the label is localized without this
// function needing React context (take-order-i18n R4.6, R4.7).
export function gstPreview(subtotal, generalSettings, translate = t) {
  const regime = String(generalSettings?.taxRegime || '').trim().toUpperCase();
  const base = Math.max(0, Math.trunc(subtotal));
  const roundHalfUp = (n) => Math.floor(n + 0.5);

  // India GST (IN_GST) and Australia GST (AU_GST) are mutually exclusive regimes
  // with identical arithmetic — a single flat rate, half-up, inclusive backs the
  // tax out of the gross and leaves the total unchanged while exclusive adds it on
  // top. They differ only in config key (`gst` vs `auGst`) and label. The IN_GST
  // branch below is byte-identical to its pre-AU form, so India behaviour and the
  // tests that pin it are untouched; AU is an additive parallel arm.
  const resolve = (cfg, inclusiveKey, exclusiveKey, inclusiveDefault) => {
    const rate = Number(cfg.defaultRatePct);
    const gstRate = Number.isFinite(rate) && rate >= 0 ? rate : 0;
    const mode = String(cfg.pricingMode || inclusiveDefault).trim().toUpperCase();
    if (mode === 'TAX_INCLUSIVE') {
      const taxable = roundHalfUp((base * 100) / (100 + gstRate));
      const taxCents = base - taxable;
      return {
        taxCents,
        totalCents: base,
        inclusive: true,
        label: translate(inclusiveKey, { rate: gstRate }),
      };
    }
    const taxCents = roundHalfUp((base * gstRate) / 100);
    return {
      taxCents,
      totalCents: base + taxCents,
      inclusive: false,
      label: translate(exclusiveKey, { rate: gstRate }),
    };
  };

  if (regime === 'IN_GST') {
    const cfg = generalSettings && typeof generalSettings.gst === 'object' ? generalSettings.gst : null;
    if (!cfg) return null;
    // India default is TAX_EXCLUSIVE.
    return resolve(cfg, 'takeOrder.gstInclusive', 'takeOrder.gstExclusive', 'TAX_EXCLUSIVE');
  }

  if (regime === 'AU_GST') {
    const cfg = generalSettings && typeof generalSettings.auGst === 'object' ? generalSettings.auGst : null;
    if (!cfg) return null;
    // Australia default is TAX_INCLUSIVE (the retail norm); the labels say GST too.
    return resolve(cfg, 'takeOrder.gstInclusive', 'takeOrder.gstExclusive', 'TAX_INCLUSIVE');
  }

  return null;
}

// US sales-tax preview, the OWN-collection counterpart to gstPreview. Mirrors the
// server (pos_order_core._us_sales_tax_cents) and the storefront (PosCartPage.jsx):
// per the WHOLE subtotal, ceil, only when the tenant runs OWN collection with a
// positive rate. Returns { taxCents, totalCents, inclusive:false, label } or null.
//
// Only ONE of gstPreview / posTaxPreview is ever non-null for a tenant: an IN_GST
// store has no posTax config, and a US store has no gst block. The caller prefers
// gstPreview and falls back to this, so the estimate shows the right tax either way.
// Still an ESTIMATE — the register/server stamps the authoritative figure; a
// multi-line ticket can differ by rounding since the server taxes PER LINE.
export function posTaxPreview(subtotal, generalSettings, translate = t) {
  const pt = generalSettings && typeof generalSettings.posTax === 'object' ? generalSettings.posTax : null;
  if (!pt || pt.enabled !== true) return null;
  if (String(pt.mode || '').trim().toUpperCase() !== 'OWN') return null;
  const rate = Number(pt.ratePercent);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  const base = Math.max(0, Math.trunc(subtotal));
  const taxCents = Math.ceil((base * rate) / 100);
  return {
    taxCents,
    totalCents: base + taxCents,
    inclusive: false,
    label: translate('takeOrder.taxEstimate', { rate }),
  };
}

// Re-exported so existing importers of this page keep working and the gate below reads the
// same rule the asterisks and hints do. The rule itself lives in utils/requiredOrderFields.js
// with a byte-identical mobile mirror — see that file for why.
export { REQUIRED_FIELD_BY_ORDER_TYPE, missingRequiredField };

export function canSubmit(state) {
  if (!state || !Array.isArray(state.lines) || state.lines.length === 0) return false;
  if (state.submitting) return false;
  // In Add_Mode the target order already owns the order type, table and party, so none
  // of those are being set and none of them gate submission — only having something to
  // add does (order-additions R5.2).
  if (state.addMode) return true;
  return missingRequiredField(state) === '';
}

/**
 * canUpdateOrder(addMode, lines, edits) -> boolean
 *
 * Gates the "Update Order" button in Add_Mode (order-line-removal follow-up,
 * superseding the earlier immediate-apply design). Two things must both hold:
 *
 *   1. Something has actually changed — a new line queued to add, OR an edit
 *      staged against an existing line (`edits` maps line index -> new quantity,
 *      only ever containing entries that DIFFER from the line's original
 *      quantity — see `stageLineEdit`). An unopened Add_Mode session with no
 *      changes must not enable the button.
 *   2. The order will not end up with ZERO items. Reducing every existing line to
 *      0 with nothing queued to add would submit an order with no items, which
 *      the backend has no defined behaviour for and which is not a real order.
 */
export function canUpdateOrder(addMode, lines, edits) {
  if (!addMode) return false;
  const hasNewLines = Array.isArray(lines) && lines.length > 0;
  const editEntries = edits && typeof edits === 'object' ? Object.entries(edits) : [];
  const hasEdits = editEntries.length > 0;
  if (!hasNewLines && !hasEdits) return false;

  const remainingExisting = (addMode.existingLines || []).reduce((count, l, idx) => {
    const staged = edits && Object.prototype.hasOwnProperty.call(edits, idx) ? edits[idx] : l.quantity;
    return count + (staged > 0 ? 1 : 0);
  }, 0);
  return remainingExisting + (hasNewLines ? lines.length : 0) > 0;
}

/**
 * stageLineEdit(edits, lineIndex, quantity, originalQuantity) -> next edits map
 *
 * Pure reducer for the local (not-yet-submitted) existing-line edit set. Setting
 * a line back to its ORIGINAL quantity removes it from the map rather than
 * storing a no-op entry — this is what lets `canUpdateOrder` tell "nothing
 * changed yet" apart from "changed and then changed back", and what stops a
 * submit from sending a pointless adjust for a line the server would treat as a
 * no-op anyway.
 */
export function stageLineEdit(edits, lineIndex, quantity, originalQuantity) {
  const next = { ...(edits || {}) };
  const q = Math.max(0, Math.trunc(Number(quantity) || 0));
  if (q === originalQuantity) {
    delete next[lineIndex];
  } else {
    next[lineIndex] = q;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Order additions (order-additions spec). Pure helpers, exported for tests.
// ---------------------------------------------------------------------------

/**
 * Build the Add_Mode descriptor from an open-tickets entry.
 *
 * Carries `subtotalCents` because the confirmation must show the order total BEFORE and
 * AFTER exactly. GST applies to the whole base, so the new total has to be derived from
 * the combined subtotal rather than by adding a separately-taxed delta — otherwise the
 * number shown is a paisa out from what the server stores.
 */
export function buildAddMode(ticket) {
  if (!ticket || typeof ticket !== 'object') return null;
  const orderNumber = String(ticket.order_number || '').trim();
  const orderTimestamp = Number(ticket.order_timestamp);
  if (!orderNumber || !Number.isFinite(orderTimestamp) || orderTimestamp <= 0) return null;
  return {
    orderNumber,
    // The number a human is shown for this order: the in-store ticket/token when the
    // tenant issues one, the order number otherwise. Resolved HERE, off the same ticket
    // record the POS queue row resolves from, so the "Updating order …" banner and the
    // update/cancel confirmations name the order by the SAME value the queue does.
    // `orderNumber` above is untouched -- it stays the identifier every API call sends.
    displayNumber: orderDisplayNumber(ticket) || orderNumber,
    hasTicket: hasStoreTicketNumber(ticket),
    orderTimestamp,
    tableLabel: String(ticket.tableLabel || '').trim(),
    partyLabel: String(ticket.partyLabel || '').trim(),
    // The field that differentiates one TO_GO ticket from another -- a to-go order
    // has no table, so without this every to-go chip rendered identically.
    customerName: String(ticket.customerName || '').trim(),
    orderType: String(ticket.orderType || '').trim().toUpperCase(),
    itemCount: Number(ticket.itemCount) || 0,
    // Itemized so the read-only "already on this order" panel can show what is
    // actually there rather than a bare count. Defaults to [] rather than undefined
    // so callers can always .map() it without a guard.
    existingLines: Array.isArray(ticket.existingLines) ? ticket.existingLines : [],
    subtotalCents: Number(ticket.subtotal_cents) || 0,
    totalCents: Number(ticket.amount_total_cents) || 0,
    billStale: Boolean(ticket.billStale),
    // Presence, not staleness: any addition invalidates a printed bill, so the
    // confirmation warning must fire on the FIRST addition after printing — when
    // nothing is stale yet. See willInvalidatePrintedBill.
    billPrintedAt: Number(ticket.billPrintedAt) || 0,
    amendmentCount: Number(ticket.amendmentCount) || 0,
  };
}

/**
 * Totals for the confirmation step: what is being added, and the order total before
 * and after.
 *
 * Runs the SAME `gstPreview` used for a new ticket over the combined subtotal, so the
 * arithmetic cannot drift between the two flows.
 */
export function additionPreview(addMode, lines, generalSettings, translate = t) {
  const addedSubtotal = subtotalCents(lines);
  const existingSubtotal = Math.max(0, Number(addMode?.subtotalCents) || 0);
  const newSubtotal = existingSubtotal + addedSubtotal;

  const before = gstPreview(existingSubtotal, generalSettings, translate);
  const after = gstPreview(newSubtotal, generalSettings, translate);

  const oldTotal = before ? before.totalCents : existingSubtotal;
  const newTotal = after ? after.totalCents : newSubtotal;

  return {
    addedSubtotal,
    existingSubtotal,
    newSubtotal,
    oldTotal,
    newTotal,
    deltaTotal: newTotal - oldTotal,
    taxLabel: after ? after.label : '',
    newTax: after ? after.taxCents : 0,
  };
}

/**
 * Request body for POST /admin/pos/orders/add-items.
 *
 * Sends a DELTA — only the new lines — never a replacement list. A full list would
 * reintroduce the lost-update problem that additions-only exists to avoid, and would
 * let a stale client silently drop a line another server had just added.
 *
 * `idempotencyKey` must be generated once per batch and REUSED across retries; that is
 * what stops a timed-out request from appending twice.
 */
export function buildAddItemsPayload(addMode, lines, idempotencyKey) {
  return {
    order_number: addMode.orderNumber,
    order_timestamp: addMode.orderTimestamp,
    idempotencyKey,
    items: (lines || []).map(buildLineItem),
  };
}

/**
 * Totals for the "Update Order" confirmation: what is being added, what is being
 * changed on existing lines, and the order total before and after — ALL in one
 * combined preview (superseding the old immediate-apply design where an existing
 * line was adjusted with its own separate API call the instant a server clicked
 * +/-, with no review step and no undo).
 *
 * `edits` maps existing-line index -> new absolute quantity, as staged by
 * `stageLineEdit`; only entries that differ from the line's original quantity are
 * ever present. Runs the SAME `gstPreview` a new ticket and a plain addition use,
 * over the fully combined subtotal, so the arithmetic cannot drift between flows.
 */
export function updatePreview(addMode, lines, edits, generalSettings, translate = t) {
  const existingLines = addMode?.existingLines || [];
  const editsDeltaCents = Object.entries(edits || {}).reduce((acc, [idxStr, qty]) => {
    const line = existingLines[Number(idxStr)];
    if (!line) return acc;
    return acc + (qty - line.quantity) * line.unitPriceCents;
  }, 0);
  const addedSubtotal = subtotalCents(lines);
  const existingSubtotal = Math.max(0, Number(addMode?.subtotalCents) || 0);
  const newSubtotal = Math.max(0, existingSubtotal + editsDeltaCents + addedSubtotal);

  const before = gstPreview(existingSubtotal, generalSettings, translate);
  const after = gstPreview(newSubtotal, generalSettings, translate);

  const oldTotal = before ? before.totalCents : existingSubtotal;
  const newTotal = after ? after.totalCents : newSubtotal;

  return {
    addedSubtotal,
    editsDeltaCents,
    existingSubtotal,
    newSubtotal,
    oldTotal,
    newTotal,
    deltaTotal: newTotal - oldTotal,
    taxLabel: after ? after.label : '',
    newTax: after ? after.taxCents : 0,
  };
}

/** Opaque per-batch idempotency token. Not security-sensitive; only uniqueness matters. */
export function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Jump-to-top / jump-to-bottom
// ---------------------------------------------------------------------------

/**
 * A fixed pair of buttons that jump the page to the top or the bottom.
 *
 * WHY: this screen is a long menu grid above a ticket pane, so on a full menu the send
 * button and the category chips end up several screens apart. Scrolling back and forth
 * between "add another item" and "send" is the single most repeated gesture on the screen.
 *
 * BOTH BUTTONS ARE ALWAYS RENDERED, and each disables itself when already at that end
 * rather than disappearing. A control that vanishes moves the other one under the finger
 * that was reaching for it, which on a tablet means mis-taps.
 *
 * Positioned bottom-LEFT. Bottom-right is where the ticket pane's own actions sit on wide
 * layouts and where a browser puts its scrollbar on narrow ones.
 */
function ScrollToEnds() {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const read = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const viewport = window.innerHeight || 0;
      const full = Math.max(
        document.body.scrollHeight || 0,
        document.documentElement.scrollHeight || 0
      );
      setAtTop(y <= 8);
      // 8px of slack: fractional device pixel ratios and a sub-pixel page height mean an
      // exact equality test never fires on some zoom levels.
      setAtBottom(y + viewport >= full - 8);
    };
    read();
    window.addEventListener('scroll', read, { passive: true });
    window.addEventListener('resize', read);
    return () => {
      window.removeEventListener('scroll', read);
      window.removeEventListener('resize', read);
    };
  }, []);

  const jump = (to) => {
    const full = Math.max(
      document.body.scrollHeight || 0,
      document.documentElement.scrollHeight || 0
    );
    window.scrollTo({ top: to === 'top' ? 0 : full, behavior: 'smooth' });
  };

  // Nothing to jump to on a page that fits the viewport, so the buttons would be noise.
  if (atTop && atBottom) return null;

  return (
    <div
      style={{
        position: 'fixed', left: 16, bottom: 16, zIndex: 900,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <button
        type="button"
        onClick={() => jump('top')}
        disabled={atTop}
        aria-label={t('takeOrder.scrollToTop')}
        title={t('takeOrder.scrollToTop')}
        style={{ ...scrollJumpBtn, opacity: atTop ? 0.4 : 1, cursor: atTop ? 'default' : 'pointer' }}
      >
        ↑
      </button>
      <button
        type="button"
        onClick={() => jump('bottom')}
        disabled={atBottom}
        aria-label={t('takeOrder.scrollToBottom')}
        title={t('takeOrder.scrollToBottom')}
        style={{ ...scrollJumpBtn, opacity: atBottom ? 0.4 : 1, cursor: atBottom ? 'default' : 'pointer' }}
      >
        ↓
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customization sheet
// ---------------------------------------------------------------------------

/**
 * The one extra step between tapping a customizable product and it landing on the ticket.
 *
 * PRESENTED AS A MODAL, not a page, so the ticket stays on screen behind it — a waiter
 * reading back an order needs to see what is already on it.
 *
 * THE ERROR PRESENTATION IS THE POINT OF THIS COMPONENT (decision D1, R4.2–R4.6). The
 * storefront answers "you skipped a required choice" with a `window.alert`, which is
 * tolerable in a desktop browser and wrong on a tablet held by someone taking an order: it
 * steals focus, says nothing about WHICH group, and truncates to three lines. Here every
 * unsatisfied group is named in a banner AND outlined in place, so the fix is visible
 * without reading a dialog.
 *
 * Errors appear only after a confirm attempt (`showErrors`), never while the waiter is still
 * choosing. A required group that is empty the instant the sheet opens is not yet a mistake.
 *
 * OPTION AND GROUP NAMES ARE NOT TRANSLATED, because `ModifierGroups` carries no i18n field
 * yet (noted in the spec's Future Extensions). The surrounding chrome is localized; the
 * names render as configured, which for a Tamil tenant means English option names in a Tamil
 * UI. Flagged rather than faked — a machine translation here would end up on a kitchen chit.
 */
function CustomizeSheet({ state, showErrors, activeLanguage, onToggle, onCancel, onConfirm }) {
  const { product, groups, selections } = state;
  const { errors } = validateSelections(groups, selections);
  const badGroupIds = showErrors ? unsatisfiedGroupIds(groups, selections) : [];
  const { modifierSummary: summary, modifierPriceCents } = summarizeSelections(groups, selections);
  const baseCents = productUnitPriceCents(product);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('takeOrder.customize.title')}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 20, width: 'min(520px, 100%)',
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(15,23,42,0.35)',
        }}
      >
        <h2 style={{ margin: '0 0 2px', fontSize: '1.05rem', color: '#0f172a' }}>
          {localizedName(product, activeLanguage) || t('takeOrder.itemFallback')}
        </h2>
        <div style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 12 }}>
          {t('takeOrder.customize.subtitle')}
        </div>

        {/* Named, not counted. "2 groups need attention" would make the waiter hunt. */}
        {showErrors && errors.length ? (
          <div
            role="alert"
            style={{
              marginBottom: 12, padding: 10, borderRadius: 8, background: '#fee2e2',
              border: '1px solid #dc2626', color: '#991b1b', fontSize: '0.82rem', fontWeight: 700,
            }}
          >
            <div>{t('takeOrder.customize.missingRequired')}</div>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontWeight: 600 }}>
              {errors.map((err) => (
                <li key={`${err.groupId}-${err.message}`}>{err.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {groups.map((group) => {
          const gid = String(group.id || '').trim();
          const options = Array.isArray(group.options) ? group.options : [];
          const selected = Array.isArray(selections?.[gid]) ? selections[gid] : [];
          const bad = badGroupIds.indexOf(gid) !== -1;
          const min = Number(group.minSelections) || 0;
          const isIncluded = String(group.groupType || '').trim().toUpperCase() === 'INCLUDED';
          const single = String(group.selectionMode || 'multiple').trim().toLowerCase() === 'single';

          return (
            <div
              key={gid}
              style={{
                marginBottom: 12, padding: 12, borderRadius: 10,
                border: bad ? '2px solid #dc2626' : '1px solid #e2e8f0',
                background: bad ? '#fef2f2' : '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>
                  {String(group.name || '').trim() || t('takeOrder.customize.optionsFallback')}
                </span>
                <span style={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 700 }}>
                  {isIncluded
                    ? t('takeOrder.customize.tapToRemove')
                    : min > 0
                      ? t('takeOrder.customize.chooseAtLeast', { count: min })
                      : t('takeOrder.customize.optional')}
                </span>
              </div>

              {options.length === 0 ? (
                // A group whose template was deleted or fully disabled. Shown rather than
                // hidden: if it is required the add is blocked, and a silent empty box would
                // make that look like a bug in the app rather than in the menu.
                <div style={{ color: '#b91c1c', fontSize: '0.78rem', marginTop: 8, fontWeight: 700 }}>
                  {t('takeOrder.customize.noOptions')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {options.map((option) => {
                    const oid = String(option.id || '').trim();
                    const on = selected.indexOf(oid) !== -1;
                    const delta = Number(option.priceDeltaCents) || 0;
                    return (
                      <button
                        key={oid}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onToggle(gid, oid)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '8px 12px', borderRadius: 999, cursor: 'pointer',
                          fontWeight: 700, fontSize: '0.85rem',
                          border: on ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                          background: on ? 'var(--admin-brand-tint, #ede9fe)' : '#fff',
                          color: on ? 'var(--admin-brand-ink, #5b21b6)' : '#334155',
                          // An INCLUDED option that has been removed reads as struck
                          // through, which is how "No onion" looks on a paper ticket.
                          textDecoration: isIncluded && !on ? 'line-through' : 'none',
                        }}
                      >
                        <span>{String(option.name || '').trim()}</span>
                        {delta > 0 ? (
                          <span style={{ color: 'var(--admin-brand, #7c3aed)', fontWeight: 800 }}>
                            +{formatMinor(delta)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {bad ? (
                <div style={{ color: '#b91c1c', fontSize: '0.78rem', marginTop: 8, fontWeight: 700 }}>
                  {errors.filter((e) => e.groupId === gid).map((e) => e.message).join(' • ')}
                </div>
              ) : null}
              {single && min === 0 && !isIncluded ? (
                <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: 6 }}>
                  {t('takeOrder.customize.tapAgainToClear')}
                </div>
              ) : null}
            </div>
          );
        })}

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
          {summary ? (
            <div style={{ color: 'var(--admin-brand, #7c3aed)', fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>
              {summary}
            </div>
          ) : null}
          {/* PREVIEW ONLY. The server re-derives this from the catalog and its figure is the
              one that gets charged (decision D4). */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, color: '#0f172a' }}>
            <span>{t('takeOrder.customize.lineTotal')}</span>
            <span>{formatMinor(baseCents + modifierPriceCents)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onCancel} style={{ ...qtyBtn, flex: 1, width: 'auto', height: 42 }}>
            {t('takeOrder.customize.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              flex: 2, height: 42, borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'var(--admin-brand, #7c3aed)', color: '#fff', fontWeight: 800, fontSize: '0.95rem',
            }}
          >
            {t('takeOrder.customize.addToTicket')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function AdminTakeOrder() {
  const navigate = useNavigate();

  // Whether this caller may cancel an order, which gates the new "Cancel Order"
  // button. Deliberately checked against 'POS Queue', not 'Take Order': cancelling
  // is a queue operation (same tab the existing Cancel action on AdminPosQueue.js is
  // gated on), so a waiter with ONLY Take Order cannot cancel from here either --
  // consistent with the split order-additions established. Convenience gating only,
  // mirroring the exact pattern AdminPosQueue.js uses for its "+ Add items" link;
  // the server enforces the real check independently via ROUTE_PERMISSIONS.
  const posQueueCapability = useAdminCapability();
  // canEditTab, NOT canEdit: the per-tab permission map is `{}` for Admin and
  // Superadmin (their access is role-based), so canEdit('POS Queue') is always
  // false for them and this button would never render for an admin.
  const canCancelOrder = posQueueCapability.loaded && !posQueueCapability.error
    ? posQueueCapability.canEditTab('POS Queue')
    : false;

  // Subscribes this page to language changes so every t() call re-renders.
  //
  // IMPORTANT: this value must NOT be used as a React key on any subtree that
  // holds ticket state. Remounting on a language change would silently discard a
  // waiter's in-progress order mid-service (R3.8, design Property 10). Ticket
  // state lives in the useState hooks below and is untouched by a language swap.
  const activeLanguage = useLanguage();

  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [products, setProducts] = useState([]);
  // Category display-name translations, keyed by English category name (R6.7).
  const [categoryNameI18n, setCategoryNameI18n] = useState({});
  const [generalSettings, setGeneralSettings] = useState(null);
  const [username, setUsername] = useState('');

  const [lines, setLines] = useState([]);
  const [orderType, setOrderType] = useState('DINE_IN');
  const [tableLabel, setTableLabel] = useState('');
  const [guestCount, setGuestCount] = useState('');
  // Table party subdivision (table-party-subdivision R3.1, R3.2). Only ever populated
  // for a DINE_IN ticket on a tenant that opted in; the submit payload omits both keys
  // in every other case, so a non-opted-in tenant sends the pre-feature body (R1.2).
  //
  // `partyTouched` records that the SERVER chose this letter, as opposed to the screen
  // pre-selecting it. Without it, auto-pre-selection would keep overwriting a
  // deliberate override every time the open-tickets list refreshed (R4.5).
  const [partyLabel, setPartyLabel] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyTouched, setPartyTouched] = useState(false);
  // The table a party ticket was just sent for, held so the success banner can offer
  // "next party at this table" (R3.6). Empty means no offer is showing.
  const [nextPartyTable, setNextPartyTable] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Client-side filter for the open-tickets picker, so a server can confine a busy list
  // to the order they want (by table, customer name, party, ticket/order number, or the
  // to-go phone last-4). Matches only fields the open-ticket projection already exposes.
  const [openTicketSearch, setOpenTicketSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // --- Order additions / order-line-removal follow-up --------------------------
  // Orders that may still be updated, and the one currently targeted.
  const [openTickets, setOpenTickets] = useState([]);
  const [addMode, setAddMode] = useState(null);
  // One key per batch, reused across retries so a timed-out request cannot append twice.
  const [addKey, setAddKey] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Review-before-send gate for a NEW ticket. The primary button opens this instead of
  // submitting, because on a narrow viewport the ticket panel is below the menu and a
  // server could reach "Send" having never seen the list they were about to send.
  // Add_Mode keeps its own `confirmOpen` review and is deliberately untouched.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Scan-to-Pay QR (take-order-scan-to-pay). `sentOrder` holds the just-created order (the
  // send path otherwise discards the response and clears the ticket), so the success banner
  // can offer "Collect by QR". `scanToPayOrder` is the order the QR modal is open for, or
  // null. Both cleared when starting a fresh ticket.
  const [sentOrder, setSentOrder] = useState(null);
  const [scanToPayOrder, setScanToPayOrder] = useState(null);
  // Staged edits against EXISTING lines: { [lineIndex]: newQuantity }. Nothing here
  // reaches the server until "Update Order" is pressed — this replaced an earlier
  // design where +/- on an existing line called the API immediately with no review
  // step and no undo, which is why the button below is enabled/disabled by whether
  // this map (or `lines`, the new-items batch) is non-empty.
  const [lineEdits, setLineEdits] = useState({});
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        // Gated on the SEPARATE 'Take Order' tab, not 'POS Queue'
        // (order-additions Requirement 2.11/2.12). The old gate borrowed
        // POS Queue: Edit, which is also the grant for connecting Square and
        // editing POS taxes — too much for a waiter.
        const access = await resolveTabAccess(session, 'Take Order');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }
        setCanEdit(Boolean(access.canEdit));
        setUsername(user?.username || user?.attributes?.email || 'Admin');

        const token = session.getIdToken().getJwtToken();
        const dataRes = await fetch(`${API_BASE_URL}/admin/pos/take-order/data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await dataRes.json().catch(() => ({}));
        if (!dataRes.ok) {
          const err = new Error(friendlyApiError(dataRes.status, data, t('takeOrder.error.loadMenu')));
          err.status = dataRes.status;
          throw err;
        }
        setProducts(Array.isArray(data?.products) ? data.products : []);
        setCategoryNameI18n(
          data?.categoryNameI18n && typeof data.categoryNameI18n === 'object'
            ? data.categoryNameI18n
            : {}
        );
        const settings = data?.settings && typeof data.settings === 'object' ? data.settings : {};
        setGeneralSettings(settings);
        setStoreCurrency(settings?.currency, settings?.locale);

        // Staff-interface languages. A tenant with no `i18n` block resolves to
        // English-only, and the device store is NOT read at all in that case —
        // the feature leaves no trace for tenants that never opted in (R1.3).
        const languageConfig = configureLanguages(settings?.i18n);
        if (languageConfig.multilingual) {
          const stored = readDeviceLanguage(LANGUAGE_TENANT_KEY);
          // A stored code naming a language the tenant has since removed is
          // discarded in favour of the tenant default (R3.5). setLanguage clamps.
          setLanguage(
            stored && languageConfig.available.includes(stored)
              ? stored
              : languageConfig.defaultLanguage
          );
        }
      } catch (err) {
        console.error('Take Order init failed:', err);
        const status = Number(err?.status);
        if (status === 401) {
          navigate('/admin/login');
          return;
        }
        setError(err?.message || t('takeOrder.error.loadData'));
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const c = String(p?.Category || '').trim();
      if (c) set.add(c);
    });
    // Ordered by the DISPLAYED name, so Tamil chips sort sensibly in Tamil (R9.5).
    return Array.from(set).sort((a, b) =>
      compareCategoryNames(a, b, categoryNameI18n, activeLanguage)
    );
  }, [products, categoryNameI18n, activeLanguage]);

  const visibleProducts = useMemo(() => {
    return products.filter((p) => {
      if (p?.OnlineOrderingDisabled || p?.onlineOrderingDisabled) {
        // still allow staff to order it; do not filter on that flag.
      }
      // Category filtering still compares the ENGLISH category value, since that is
      // what the product row carries; only the chip's label is translated.
      if (activeCategory && String(p?.Category || '').trim() !== activeCategory) return false;
      // Matches the localized name OR the English one, so a Tamil query and an
      // English query both find the dish (R9.4).
      return matchesSearch(p, searchQuery, activeLanguage);
    });
  }, [products, activeCategory, searchQuery, activeLanguage]);

  const sub = useMemo(() => subtotalCents(lines), [lines]);
  // Tax preview: GST for an IN_GST store, US sales tax for an OWN-collection store.
  // Only one is ever non-null; prefer GST then fall back to US sales tax so the cart
  // estimate shows the same tax the server will stamp instead of subtotal-only.
  const preview = useMemo(
    () => gstPreview(sub, generalSettings) || posTaxPreview(sub, generalSettings),
    [sub, generalSettings]
  );
  const totalCents = preview ? preview.totalCents : sub;
  const submitState = { lines, orderType, tableLabel, customerName, submitting, addMode: !!addMode };
  // Which mandatory field is still blank, for the asterisk, the red outline and the hint.
  const missingField = missingRequiredField(submitState);
  // In Add_Mode, "can submit" is governed by canUpdateOrder (something changed AND
  // the result is not empty) rather than canSubmit, which only asks "is there a new
  // line" — that alone would leave the button dead for a pure removal with nothing
  // being added.
  const submitEnabled =
    canEdit && (addMode ? canUpdateOrder(addMode, lines, lineEdits) : canSubmit(submitState));
  const addPreview = useMemo(
    () => (addMode ? updatePreview(addMode, lines, lineEdits, generalSettings) : null),
    [addMode, lines, lineEdits, generalSettings]
  );

  // --- Inventory tracking (pos-inventory-tracking R6.2, R7.3) ------------------
  //
  // The gate comes from the Take Order projection's `autoInventoryEnabled`, which the server
  // sets only when the tenant opted in. `Stock` is absent from every product when the flag is
  // off, so both helpers below are inert without needing their own gate check — but the gate
  // is still tested explicitly, because "inert because the data is missing" is a weaker
  // guarantee than "inert because we checked".
  const inventoryEnabled = generalSettings?.autoInventoryEnabled === true;

  // Remaining count for a product, or null when untracked or the flag is off.
  // The server omits `Stock` for untracked rows (R7.2), so `null` here means exactly
  // "no count to show" and never "zero left".
  const stockOf = (p) => {
    if (!inventoryEnabled) return null;
    const raw = p?.Stock;
    if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
    return Math.trunc(raw);
  };

  // A tracked product at zero is unavailable, using the SAME presentation as an
  // administrator's explicit `OnlineOrderingDisabled` rather than a second availability
  // concept (R6.2, decision D6). Derived at read time, never written, so replenishment
  // restores availability with no clear-on-replenish step (R6.4, R6.5).
  const isSoldOut = (p) => stockOf(p) === 0;

  // --- Table party subdivision -------------------------------------------------
  //
  // The gate. Resolved from the Take Order settings projection, which omits the
  // `tableParties` block entirely for a tenant that never opted in, so this is false
  // for them and for every render before settings load (R1.11, fail-closed).
  const partiesEnabled = tablePartiesEnabled(generalSettings);
  // Never in Add_Mode: appending to an existing ticket inherits that ticket's party,
  // and offering a party picker there would imply the append could move it.
  const partyControlVisible = partiesEnabled && orderType === 'DINE_IN' && !addMode;

  // Open tickets already seated at the table being typed. Uses the PROJECTION variant
  // deliberately -- see dineInTicketsAtTable: the open-tickets endpoint has already
  // applied openness server-side and its response omits the fields isOpenTicket reads,
  // so re-deriving openness here would filter every ticket away and silently report
  // an empty table.
  const partyTicketsAtTable = useMemo(
    () => (partyControlVisible ? dineInTicketsAtTable(openTickets, tableLabel) : []),
    [partyControlVisible, openTickets, tableLabel]
  );
  const partyLabelsInUse = useMemo(
    () => usedPartyLabels(partyTicketsAtTable),
    [partyTicketsAtTable]
  );
  const suggestedPartyLabel = useMemo(
    () => nextPartyLabelFromTickets(openTickets, tableLabel),
    [openTickets, tableLabel]
  );

  // NO PARTY IS THE DEFAULT. A fresh ticket starts with no letter selected, and the waiter
  // picks one only when a table genuinely holds more than one group.
  //
  // This used to pre-select the lowest unused label unconditionally, which meant every
  // ordinary single-group dine-in ticket was stamped "Party A". That is noise on the great
  // majority of tickets: it puts a letter on a chit nobody asked for, and it makes the
  // letter meaningless as a signal, because seeing "A" no longer tells you the table was
  // subdivided at all.
  //
  // The suggestion is still computed and still applied, but ONLY for the one flow that has
  // actually established there are multiple parties: "next party at this table" (R3.6).
  // `partyPreselectForTable` records the table that flow armed it for.
  //
  // KEYED ON THE TABLE, not a boolean, for an ordering reason. `startNextPartyAtTable` sets
  // the table label in the same batch as arming this, and the table-change effect below runs
  // afterwards — a boolean flag would be cleared by that effect before the pre-selection
  // effect ever saw it. Keying on the table also self-clears: type a different table and the
  // comparison stops matching, so the arm cannot leak onto an unrelated ticket.
  const [partyPreselectForTable, setPartyPreselectForTable] = useState('');

  useEffect(() => {
    if (!partyControlVisible) return;
    if (partyTouched) return;
    if (!partyPreselectForTable) return;
    const armed = String(partyPreselectForTable).trim().toLowerCase().replace(/\s+/g, ' ');
    const current = String(tableLabel || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!armed || armed !== current) return;
    setPartyLabel(suggestedPartyLabel);
  }, [partyControlVisible, partyTouched, partyPreselectForTable, tableLabel, suggestedPartyLabel]);

  // Changing the table abandons any letter chosen for the previous one: a party label is
  // scoped to the table it was picked at and carries no identity across tables (R10.1).
  useEffect(() => {
    setPartyTouched(false);
    setPartyLabel('');
  }, [tableLabel]);

  const choosePartyLabel = (label) => {
    setPartyTouched(true);
    setPartyLabel(label);
  };

  // A collision is a WARNING, never a block (R4.6). The server may genuinely be adding
  // a second ticket for party A, and the lookup is a point-in-time read with no lock,
  // so refusing on its say-so would be refusing on stale information.
  const partyCollision = Boolean(
    partyControlVisible
      && partyLabel
      && partyLabelsInUse.includes(String(partyLabel).trim().toUpperCase())
  );

  // Refresh the open-tickets strip. FAIL SOFT: a failure hides the strip and never
  // blocks new-order entry, which is the primary use of this screen (R3.6).
  const refreshOpenTickets = React.useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/open-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setOpenTickets([]);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setOpenTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch {
      setOpenTickets([]);
    }
  }, []);

  // Re-read the menu projection so the stock badges reflect what the write just did
  // (pos-inventory-tracking R7.3). Without this the counts are whatever they were when the
  // screen mounted, and a waiter watching "2 left" send two orders would see it stay at 2
  // until a manual reload — the number would be actively misleading rather than merely stale.
  //
  // GATED ON `inventoryEnabled`. This endpoint scans the catalog, so firing it after every
  // order for a tenant that does not track stock would add a scan per order and change
  // nothing on screen. A flag-off tenant must behave exactly as it did before this feature
  // (R1.2), and that includes making no extra requests.
  //
  // Replaces `products` ONLY. Re-applying `settings` would re-run the language-configuration
  // effect in the middle of a shift, and the gate is not something that changes between two
  // orders on one screen.
  //
  // FAILS SOFT, deliberately. A stale badge is a display problem; the server still refuses
  // an oversell atomically, so nothing can be sold that is not there. Surfacing an error
  // here would put a scary banner on a shift where the order itself succeeded.
  const refreshStockCounts = React.useCallback(async () => {
    if (!inventoryEnabled) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/take-order/data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (Array.isArray(data?.products)) setProducts(data.products);
    } catch {
      // keep the counts already on screen
    }
  }, [inventoryEnabled]);

  // Refresh counts when the tab regains focus, for parity with the mobile surface (which
  // gets this from its screen-focus effect).
  //
  // THIS COVERS THE CASE A POST-WRITE REFRESH CANNOT: another terminal, or a waiter's phone,
  // sold the last portion. Nothing on this screen wrote, so nothing triggered a re-read, and
  // a browser tab left open across a service would show counts from whenever it was opened.
  // Tying it to focus rather than a poll means no request while the tab sits unused.
  useEffect(() => {
    if (!inventoryEnabled) return undefined;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      refreshStockCounts();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [inventoryEnabled, refreshStockCounts]);

  // Re-read the strip immediately AND once more after a short delay, for use after any
  // write to an order.
  //
  // /admin/pos/orders/open-tickets queries the `gsi1` GLOBAL SECONDARY INDEX, which is
  // eventually consistent — DynamoDB does not accept ConsistentRead on a GSI. A read
  // fired straight after an update can therefore return the pre-update order, and the
  // strip would hold that stale copy until something else refetched. Re-opening the
  // ticket then showed the old lines even though the POS queue had the new ones.
  //
  // The delayed second read mitigates replication lag; it does not guarantee freshness.
  //
  // STOCK RIDES ALONG ON BOTH PASSES. Every call site of this function is a write that can
  // move stock — creating an order decrements, adjusting or removing a line restores, and
  // cancelling restores — so the two refreshes belong together rather than being sprinkled
  // across five call sites where one would eventually be missed. The catalog projection uses
  // a `scan`, which is also eventually consistent, so it benefits from the delayed second
  // read for the same reason the strip does.
  const refreshTimerRef = React.useRef(null);
  const refreshOpenTicketsAfterWrite = React.useCallback(async () => {
    await Promise.all([refreshOpenTickets(), refreshStockCounts()]);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refreshOpenTickets();
      refreshStockCounts();
    }, 1500);
  }, [refreshOpenTickets, refreshStockCounts]);

  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  useEffect(() => {
    if (!canEdit) return;
    refreshOpenTickets();
  }, [canEdit, refreshOpenTickets]);

  // Deep link from the POS Queue "+ Add items" button (order-additions R4.8).
  //
  // The dependency between the two screens is exactly this: a route parameter. Take
  // Order owns Add_Mode because the menu grid and ticket builder only exist here;
  // the queue only names an order. No shared state, no shared components, and Take
  // Order never imports from the 5,000-line queue file.
  //
  // Runs once the strip has loaded so the target's totals come from the same
  // authoritative projection a manual selection would use.
  const deepLinkAppliedRef = React.useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!canEdit || addMode || openTickets.length === 0) return;
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }
    const wantNumber = String(params.get('addTo') || '').trim();
    if (!wantNumber) return;
    const wantAt = String(params.get('at') || '').trim();
    const match = openTickets.find(
      (tk) =>
        String(tk.order_number || '') === wantNumber &&
        (!wantAt || String(tk.order_timestamp || '') === wantAt)
    );
    deepLinkAppliedRef.current = true;
    if (match) {
      enterAddMode(match);
    } else {
      // Settled, cancelled, or outside the window between the queue render and arriving
      // here. Say so rather than silently landing on a blank new ticket.
      setError(t('takeOrder.add.error.noLongerOpen'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, addMode, openTickets]);

  const enterAddMode = (ticket) => {
    const next = buildAddMode(ticket);
    if (!next) return;
    setError('');
    setMessage('');
    setLastResult(null);
    // Start a fresh batch: the pending additions belong to THIS target order.
    setLines([]);
    setLineEdits({});
    setAddKey(newIdempotencyKey());
    setAddMode(next);
  };

  const leaveAddMode = () => {
    setAddMode(null);
    setAddKey('');
    setConfirmOpen(false);
    setLastResult(null);
    // Discards the pending batch AND any staged existing-line edits (R4.7).
    setLines([]);
    setLineEdits({});
  };

  // "Create New Order" in the Add_Mode banner. leaveAddMode() throws away a queued
  // batch and any staged line edits with no undo, so confirm when there is something
  // to lose. A WRAPPER rather than a guard inside leaveAddMode(): the other caller is
  // the post-cancel cleanup, which runs once the order is already cancelled and would
  // then be asking the waiter to protect items on an order that no longer exists.
  // Mirrors the mobile app so the two clients cannot diverge on a data-loss prompt.
  const requestLeaveAddMode = () => {
    const hasPendingWork = lines.length > 0 || Object.keys(lineEdits).length > 0;
    if (hasPendingWork && !window.confirm(t('takeOrder.add.exitBody'))) return;
    leaveAddMode();
  };

  // Owner decisions this encodes (order-line-removal follow-up to order-additions):
  //   D1(a) no new permission -- same Take Order: Edit grant that lets a server add.
  //   D2(c) no batch/print lock -- any existing line, any time before settlement.
  //   D5    partial-quantity edits, not just whole-line removal.
  //
  // Staged locally, NOT sent to the server on click. An earlier version called
  // /adjust-line immediately on every +/-, with no review step and no way back —
  // a removal took effect the instant it was clicked, which looked like nothing
  // happened without watching closely, and offered no chance to reconsider. Now the
  // edit sits in `lineEdits` until "Update Order" is pressed, exactly like a NEW
  // item sits in `lines` until then.
  const editExistingLine = (lineIndex, newQuantity) => {
    const original = addMode?.existingLines?.[lineIndex]?.quantity ?? 0;
    setLineEdits((prev) => stageLineEdit(prev, lineIndex, newQuantity, original));
  };

  // Customization sheet state. `null` means closed, which is the state for every product
  // that has no modifier groups — i.e. every product on `customer2` and most on the others.
  // R2.2: those keep the one-tap add they have today, with no extra dialog in the way.
  const [customizing, setCustomizing] = useState(null);
  const [showCustomizeErrors, setShowCustomizeErrors] = useState(false);

  const onAdd = (product) => {
    // Blocked client-side for immediacy; the server refuses it too (R6.3), so this is a
    // convenience rather than the enforcement point.
    if (isSoldOut(product)) return;
    setMessage('');
    if (hasModifiers(product)) {
      const groups = groupsOf(product);
      setShowCustomizeErrors(false);
      // Seeded, not empty: a required single-select arrives already answered with its
      // default, so the common case is confirm-and-go rather than a puzzle to solve
      // (design Property 3).
      setCustomizing({ product, groups, selections: seedSelections(groups) });
      return;
    }
    setLines((prev) => addLine(prev, product));
  };

  const onToggleOption = (groupId, optionId) => {
    setCustomizing((prev) => (prev
      ? { ...prev, selections: toggleSelection(prev.groups, prev.selections, groupId, optionId) }
      : prev));
  };

  const closeCustomize = () => {
    setCustomizing(null);
    setShowCustomizeErrors(false);
  };

  const confirmCustomize = () => {
    if (!customizing) return;
    const { ok } = validateSelections(customizing.groups, customizing.selections);
    if (!ok) {
      // BLOCKS the add and shows every unsatisfied group inline (decision D1). Deliberately
      // no window.alert: the storefront uses one, which is fine in a desktop browser and
      // wrong on a tablet held by someone mid-order.
      setShowCustomizeErrors(true);
      return;
    }
    setLines((prev) => addLine(prev, customizing.product, customizing.selections));
    closeCustomize();
  };

  const onQty = (lineId, qty) => setLines((prev) => setLineQuantity(prev, lineId, qty));
  const onNotes = (lineId, notes) => setLines((prev) => setLineNotes(prev, lineId, notes));
  const onClear = () => {
    setLines([]);
    setTableLabel('');
    setGuestCount('');
    setPartyLabel('');
    setPartyName('');
    setPartyTouched(false);
    setPartyPreselectForTable('');
    setCustomerName('');
    setCustomerPhone('');
    setOrderNotes('');
  };

  // "Next party at this table" (R3.6). The deliberate exception to onClear: RESTORE the
  // table the last ticket was sent for, and drop only what belonged to that group.
  //
  // This is what makes separating two groups not depend on the server remembering to.
  // The party letter is left blank rather than advanced by hand — the pre-selection
  // effect recomputes it from the refreshed open-tickets list, which is exactly the
  // path a cold start takes. One rule for "which letter is next" instead of two that
  // can disagree.
  //
  // THIS IS THE ONE FLOW THAT ARMS PRE-SELECTION. Elsewhere the default is No party; here
  // the waiter has explicitly said a second group is sitting at this table, so suggesting
  // the next free letter is the whole point of the button.
  const startNextPartyAtTable = () => {
    const table = nextPartyTable;
    setNextPartyTable('');
    setLines([]);
    setGuestCount('');
    setPartyLabel('');
    setPartyName('');
    setPartyTouched(false);
    setPartyPreselectForTable(table);
    setCustomerName('');
    setCustomerPhone('');
    setOrderNotes('');
    setMessage('');
    setTableLabel(table);
  };

  const sendToKitchen = async () => {
    if (!submitEnabled) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const gc = String(guestCount || '').trim();
      const payload = {
        orderType,
        // serverId/serverName intentionally omitted — derived server-side from claims.
        items: lines.map(buildLineItem),
        ...(orderType === 'DINE_IN' ? { tableLabel: tableLabel.trim() } : {}),
        ...(gc ? { guestCount: Number(gc) } : {}),
        // Party fields are sent ONLY for a dine-in ticket on an opted-in tenant, and
        // only when non-empty (R1.2, R3.3). A tenant that never opted in therefore
        // sends the byte-identical pre-feature body — the request is the same shape it
        // was before this feature existed, not the same shape with two nulls in it.
        ...(partyControlVisible && partyLabel.trim() ? { partyLabel: partyLabel.trim() } : {}),
        ...(partyControlVisible && partyName.trim() ? { partyName: partyName.trim() } : {}),
        ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
        ...(customerPhone.trim() ? { customerPhone: customerPhone.trim() } : {}),
        ...(orderNotes.trim() ? { notes: orderNotes.trim() } : {}),
      };
      const res = await fetch(`${API_BASE_URL}/admin/pos/orders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(friendlyApiError(res.status, data, t('takeOrder.error.sendFailed')));
        return; // keep the ticket so staff can retry
      }
      // Captured BEFORE onClear wipes it, so the success banner can offer to start the
      // next party at the same table (R3.6). Only offered for a ticket that actually
      // carried a party — on every other order this stays empty and the banner is
      // exactly what it was before this feature.
      const sentPartyTable =
        partyControlVisible && partyLabel.trim() ? tableLabel.trim() : '';
      onClear();
      // Only on success. The `return` above keeps the review modal open on a failed
      // send so the error is read where the server is already looking, with the
      // ticket still intact behind it to retry.
      setReviewOpen(false);
      setNextPartyTable(sentPartyTable);
      // The token the waiter should call out, when the tenant issues one -- that is the
      // whole point of the feature, and the confirmation is where they read it. Falls back
      // to the order number, so a tenant with the feature off sees the identical message.
      const ticketNum = readStoreTicketNumber(data);
      const num = ticketNum || String(data?.order_number || '').trim();
      setMessage(
        num
          ? t(ticketNum ? 'takeOrder.sentWithTicket' : 'takeOrder.sentWithNumber', { orderNumber: num })
          : t('takeOrder.sent')
      );
      // Capture the just-created order so the success banner can offer "Collect by QR"
      // (take-order-scan-to-pay). onClear() above wiped the live ticket, so the fields the
      // scan-to-pay modal needs are carried from the response. A fresh staff order is always
      // unpaid PAY_AT_POS by construction; the response omits those fields, so they are
      // stamped here for canCollectScanToPay to pass. posProvider is echoed only for
      // SQUARE/CLOVER staff orders; a Stripe tenant relies on the generalSettings fallback.
      setSentOrder(
        data && typeof data === 'object' && data.order_number
          ? {
              order_number: String(data.order_number || '').trim(),
              order_timestamp: data.order_timestamp,
              amount_total_cents: Number(data.amount_total_cents || 0),
              currency: String(data.currency || 'USD').toUpperCase(),
              posProvider: String(data.posProvider || '').trim().toUpperCase(),
              posPaymentMode: 'PAY_AT_POS',
              payment_status: 'pending_payment',
            }
          : null
      );
      // A new order is itself addable, so refresh the strip.
      refreshOpenTicketsAfterWrite();
    } catch (err) {
      console.error('Send to kitchen failed:', err);
      setError(t('takeOrder.error.network.web'));
    } finally {
      setSubmitting(false);
    }
  };

  // Submit BOTH the staged existing-line edits and the pending new-items batch to
  // the targeted order in one action ("Update Order").
  //
  // Edits are applied one line at a time via /adjust-line (that endpoint's contract
  // is a single line index + absolute quantity), THEN new items are appended via
  // /add-items if there are any — additions go last so an edit failure never lands
  // a partial batch of unrelated new items on the order. Everything is preserved on
  // failure (R5.10) so a server can retry; `addKey` is reused across retries of the
  // additions half so a retried append can never double-apply. Retrying the edits
  // half is inherently safe too, since /adjust-line takes an absolute target
  // quantity — resubmitting the same edit twice is a no-op the second time.
  const updateOrder = async () => {
    if (!addMode || !submitEnabled) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      let latest = null;
      for (const [idxStr, quantity] of Object.entries(lineEdits)) {
        const res = await fetch(`${API_BASE_URL}/admin/pos/orders/adjust-line`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            order_number: addMode.orderNumber,
            order_timestamp: addMode.orderTimestamp,
            lineIndex: Number(idxStr),
            quantity,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setConfirmOpen(false);
          if (res.status === 409) {
            setAddMode(null);
            setError(t('takeOrder.add.error.noLongerOpen'));
            refreshOpenTicketsAfterWrite();
            return;
          }
          setError(friendlyApiError(res.status, data, t('takeOrder.add.error.adjustFailed')));
          return; // keep every staged edit AND the new-items batch so staff can retry
        }
        latest = data;
      }

      if (lines.length > 0) {
        const res = await fetch(`${API_BASE_URL}/admin/pos/orders/add-items`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(buildAddItemsPayload(addMode, lines, addKey)),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setConfirmOpen(false);
          // A 409 NOW MEANS TWO DIFFERENT THINGS, and they need opposite handling
          // (pos-inventory-tracking R4.8).
          //
          // Insufficient stock is RECOVERABLE: the ticket is still open and reducing the
          // quantity will succeed. Discriminated by the `oversell` / `soldOut` body the
          // stock refusal carries; the settled/cancelled 409 carries neither.
          //
          // THE IDEMPOTENCY KEY MUST BE REGENERATED HERE. The server reserves the key
          // BEFORE it reads the order, so a rejected request has already consumed it.
          // Resubmitting the corrected batch with the same key makes the server report a
          // duplicate and return the CURRENT order state as a success — the waiter would
          // see "sent" and the items would never be added. A silently dropped batch is far
          // worse than the error it replaces.
          if (res.status === 409 && (data?.oversell || data?.soldOut)) {
            setAddKey(newIdempotencyKey());
            setError(
              String(data?.error || '').trim() || t('takeOrder.add.error.failed')
            );
            // Stay in Add_Mode and KEEP the batch: the waiter only needs to lower a
            // quantity, not rebuild the round.
            refreshOpenTicketsAfterWrite();
            return;
          }
          // The original meaning: the order was settled or cancelled while the batch was
          // being built. Retrying cannot succeed, so leave Add_Mode. The edits half, if
          // any, already applied by this point.
          if (res.status === 409) {
            setAddMode(null);
            setError(t('takeOrder.add.error.noLongerOpen'));
            refreshOpenTicketsAfterWrite();
            return;
          }
          setConfirmOpen(false);
          setError(friendlyApiError(res.status, data, t('takeOrder.add.error.failed')));
          return; // keep the new-items batch so staff can retry with the SAME key;
          // the edits half already applied and will no-op harmlessly on a retry.
        }
        latest = data;
      }

      setConfirmOpen(false);
      setLastResult({
        orderNumber: addMode.orderNumber,
        addedLines: lines,
        totalCents: Number(latest?.amount_total_cents) || 0,
        billStale: Boolean(latest?.billStale),
      });
      setMessage(
        t(addMode.hasTicket ? 'takeOrder.add.sentTicket' : 'takeOrder.add.sent', {
          orderNumber: addMode.displayNumber,
        })
      );

      // LEAVE Add_Mode on success and return to a clean new-order screen.
      //
      // Previously this cleared only `lines`/`lineEdits` and stayed in Add_Mode, which
      // left the "already on this order" panel rendering `addMode.existingLines` — the
      // snapshot taken when Add_Mode was ENTERED, now one update out of date. So the
      // screen showed the pre-update order as though nothing had happened, and the
      // obvious next action (start the next ticket) required manually backing out.
      //
      // Deliberately NOT `leaveAddMode()`: that nulls `lastResult`, and the result
      // panel is what confirms the new order total and whether the printed bill went
      // stale. It renders outside the `addMode` branch, so it survives this reset.
      setAddMode(null);
      setAddKey('');
      setLines([]);
      setLineEdits({});
      // Ticket fields belong to the order just updated, not to the next one. Safe to
      // clear: entering Add_Mode already discarded any half-built new order.
      onClear();
      refreshOpenTicketsAfterWrite();
    } catch (err) {
      console.error('Update order failed:', err);
      setConfirmOpen(false);
      setError(t('takeOrder.error.network.web'));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelCurrentOrder = async () => {
    if (!addMode || cancelling) return;
    const ok = window.confirm(
      t(addMode.hasTicket ? 'takeOrder.add.cancelOrderConfirmTicket' : 'takeOrder.add.cancelOrderConfirm', {
        orderNumber: addMode.displayNumber,
      })
    );
    if (!ok) return;
    setCancelling(true);
    setError('');
    setMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: addMode.orderNumber,
          order_timestamp: addMode.orderTimestamp,
          reason: '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(friendlyApiError(res.status, data, t('takeOrder.add.error.cancelFailed')));
        return;
      }
      setMessage(
        t(addMode.hasTicket ? 'takeOrder.add.cancelledTicket' : 'takeOrder.add.cancelled', {
          orderNumber: addMode.displayNumber,
        })
      );
      leaveAddMode();
      refreshOpenTicketsAfterWrite();
    } catch (err) {
      console.error('Cancel order failed:', err);
      setError(t('takeOrder.error.network.web'));
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Per-device language choice. Persisted so this terminal keeps its language
  // across reloads, while the terminal next to it keeps its own (R3.3, R3.4).
  const onChangeLanguage = (code) => {
    const applied = setLanguage(code);
    writeDeviceLanguage(LANGUAGE_TENANT_KEY, applied);
  };

  const languageSwitcher = isMultilingual() ? (
    <select
      value={activeLanguage}
      onChange={(e) => onChangeLanguage(e.target.value)}
      aria-label={t('takeOrder.languageSwitcher.label')}
      title={t('takeOrder.languageSwitcher.label')}
      style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontWeight: 600 }}
    >
      {getAvailableLanguages().map((code) => (
        <option key={code} value={code}>
          {LANGUAGE_REGISTRY[code] ? LANGUAGE_REGISTRY[code].label : code}
        </option>
      ))}
    </select>
  ) : null;

  const pageHeader = (
    <header className="admin-header">
      <div className="admin-header-left">
        <div className="admin-header-logo">
          <i className="fas fa-sitemap"></i>
          <span>{PROJECT_NAME}</span>
        </div>
        <div>
          <h1>{t('takeOrder.title')}</h1>
          <p>{t('takeOrder.subtitle.web')}</p>
        </div>
      </div>
      <div className="admin-header-right">
        <AdminNavTabs />
        {languageSwitcher}
        <span className="admin-user">
          <i className="fas fa-user-shield"></i>
          {username}
        </span>
        <button onClick={handleLogout} className="btn-logout">
          <i className="fas fa-sign-out-alt"></i>
          {t('takeOrder.logout')}
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="admin-dashboard">
        {pageHeader}
        <div style={{ padding: 24, color: '#64748b' }}>{t('takeOrder.loadingMenu')}</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {pageHeader}
      <div style={{ padding: '16px 20px' }}>
        {!canEdit ? (
          <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>
            {t('takeOrder.viewOnly.web')}
          </div>
        ) : null}
        {error ? (
          <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>
            {error}
          </div>
        ) : null}
        {message ? (
          <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', marginBottom: 12 }}>
            {message}
            {/* "Next party at this table" (R3.6). Offered, not applied: the server may
                be done with that table. Present only when the ticket just sent actually
                carried a party, so every other order's success banner is unchanged. */}
            {nextPartyTable ? (
              <button
                type="button"
                onClick={startNextPartyAtTable}
                style={{
                  marginLeft: 12,
                  padding: '4px 10px',
                  borderRadius: 8,
                  border: '1px solid #16a34a',
                  background: '#fff',
                  color: '#166534',
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {t('takeOrder.party.nextParty')}
              </button>
            ) : null}
            {/* Collect by QR (take-order-scan-to-pay). The order taker can collect NOW by
                showing a QR the customer scans and pays on their phone — the key Take Order
                use case (terminal-free, no POS-queue round-trip). Shown only when the tenant
                opted in AND the just-sent order resolves to a hosted-checkout provider. The
                gate reuses the same pure resolver the queue copy uses; generalSettings carries
                scanToPayEnabled + posProvider (surfaced on the take-order-data response). */}
            {sentOrder && canCollectScanToPay(sentOrder, generalSettings) ? (
              <button
                type="button"
                onClick={() => setScanToPayOrder(sentOrder)}
                style={{
                  marginLeft: 12,
                  padding: '4px 10px',
                  borderRadius: 8,
                  border: '1px solid #16a34a',
                  background: '#fff',
                  color: '#166534',
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Collect Payment by QR
              </button>
            ) : null}
          </div>
        ) : null}

        {scanToPayOrder ? (
          <ScanToPayQrModal
            order={scanToPayOrder}
            onClose={() => setScanToPayOrder(null)}
            onPaid={(paid) => {
              // Scan-to-pay settles asynchronously via the provider webhook; the modal calls
              // this only once its poll observed the order flip to paid. The modal shows its
              // own success state on top.
              //
              // A PAID order is no longer addable (pos_order_core.is_addable_order), so:
              // refresh the open-tickets strip so it drops out, and if we are in Update Order
              // (Add_Mode) for this same order, leave it rather than leaving a closed order
              // and its "Collect Payment by QR" button on screen. The QR button is disabled
              // while edits are pending, so no unsent items are discarded here.
              const on = String(scanToPayOrder?.order_number || '').trim();
              if (!paid) return;
              setMessage(on ? `Order ${on} paid.` : 'Payment received.');
              const ots = String(scanToPayOrder?.order_timestamp ?? '');
              if (addMode && String(addMode.orderNumber) === on && String(addMode.orderTimestamp) === ots) {
                leaveAddMode();
              }
              if (canEdit) refreshOpenTicketsAfterWrite();
            }}
          />
        ) : null}

        {/* Layout moved to .take-order-layout in AdminDashboard.css. It was an
            inline grid, which media queries cannot override, so the 320px
            minimum on the order panel survived at phone widths and overflowed
            the viewport. Desktop rendering is unchanged. */}
        <div className="take-order-layout">
          {/* Menu */}
          <div>
            {/* Open tickets that can still be added to (order-additions R3.4).
                A LIST rather than a bare "Add items" button: a button would have to ask
                "which order?" on a second screen, whereas tapping a chip is one action.
                Hidden entirely when there is nothing to add to, so the common
                new-order path is untouched. */}
            {canEdit && !addMode && openTickets.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  {t('takeOrder.add.openTickets')}
                </div>
                {/* Filter the open tickets by any human-meaningful field. Pure client-side
                    over the already-loaded list.

                    Shown whenever the list is non-empty. It was gated on `> 1` to avoid
                    chrome over a single row, but open tickets are also bounded by
                    OPEN_TICKETS_LOOKBACK_SECONDS (24h by default), so a tenant routinely has
                    exactly one in-window ticket and the box never appeared. */}
                {openTickets.length > 0 ? (
                  <input
                    className="pos-input"
                    type="text"
                    value={openTicketSearch}
                    onChange={(e) => setOpenTicketSearch(e.target.value)}
                    placeholder={t('takeOrder.add.searchOpen')}
                    style={{ width: '100%', marginBottom: 8 }}
                    aria-label="Search open orders"
                  />
                ) : null}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {openTickets.filter((tk) => {
                    const term = String(openTicketSearch || '').trim().toLowerCase();
                    if (!term) return true;
                    const hay = [
                      tk.tableLabel,
                      tk.customerName,
                      tk.partyLabel,
                      tk.partyName,
                      tk.storeTicketNumber,
                      tk.order_number,
                      tk.customerPhoneLast4,
                    ]
                      .map((v) => String(v || '').toLowerCase())
                      .join(' ');
                    return hay.includes(term);
                  }).map((tk) => {
                    const label = [
                      String(tk.tableLabel || '').trim()
                        ? t('takeOrder.add.tableChip', { table: tk.tableLabel })
                        : t('takeOrder.orderType.toGo'),
                      String(tk.partyLabel || '').trim(),
                      // Differentiates one to-go ticket from another, which has no
                      // table to tell them apart by.
                      String(tk.customerName || '').trim(),
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <button
                        key={`${tk.order_number}-${tk.order_timestamp}`}
                        type="button"
                        onClick={() => enterAddMode(tk)}
                        title={t('takeOrder.add.openTicketHint')}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          gap: 2, padding: '8px 12px', borderRadius: 10,
                          border: '1px solid #cbd5e1', background: '#fff',
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                          + {label}
                        </span>
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                          {formatMinor(Number(tk.amount_total_cents) || 0)}
                          {' · '}
                          {t('takeOrder.add.itemCount', { count: Number(tk.itemCount) || 0 })}
                          {tk.billStale ? ` · ${t('takeOrder.add.billStaleShort')}` : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <input
              className="pos-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('takeOrder.search')}
              style={{ width: '100%', marginBottom: 12 }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                className={`admin-tab${activeCategory === '' ? ' active' : ''}`}
                onClick={() => setActiveCategory('')}
                style={chipStyle(activeCategory === '')}
              >
                {t('takeOrder.allCategories')}
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveCategory(c)}
                  style={chipStyle(activeCategory === c)}
                >
                  {/* Label is translated; the filter value `c` stays English (R9.3). */}
                  {localizedCategoryName(c, categoryNameI18n, activeLanguage)}
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {visibleProducts.map((p) => {
                const pid = String(p?.ProductID || '').trim();
                const inTicket = lines.find((l) => l.productId === pid);
                return (
                  <button
                    key={p.ProductID}
                    type="button"
                    onClick={() => onAdd(p)}
                    disabled={!canEdit || isSoldOut(p)}
                    style={{
                      ...menuCard,
                      cursor: (canEdit && !isSoldOut(p)) ? 'pointer' : 'not-allowed',
                      opacity: (canEdit && !isSoldOut(p)) ? 1 : 0.5,
                      borderColor: inTicket ? 'var(--admin-brand, #7c3aed)' : '#eef2f7',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: '#0f172a',
                        lineHeight: 1.25,
                        fontSize: '0.95rem',
                        // Tamil runs ~20-30% wider than English; allow long names to
                        // wrap inside the card instead of overflowing it (R11.4).
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {/* DISPLAY only. The submitted line name stays English — see
                          addLine — so the server and every downstream order consumer
                          receive an unchanged English name (R9.6). */}
                      {localizedName(p, activeLanguage) || t('takeOrder.itemFallback')}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                      <span style={{ color: 'var(--admin-brand, #7c3aed)', fontWeight: 800 }}>
                        {formatMinor(productUnitPriceCents(p))}
                      </span>
                      <span style={menuPlus}>+</span>
                    </div>
                    {/* Remaining count (R7.3). Staff-only: this number reaches the Take Order
                        projection and no storefront response (R7.4). Rendered only for a
                        TRACKED product on an opted-in tenant — `stockOf` returns null
                        otherwise, so an untracked dish shows nothing rather than "0 left". */}
                    {stockOf(p) !== null ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: isSoldOut(p) ? '#b91c1c' : stockOf(p) <= 2 ? '#b45309' : '#475569',
                        }}
                      >
                        {isSoldOut(p)
                          ? t('takeOrder.stock.soldOut')
                          : t('takeOrder.stock.remaining', { count: stockOf(p) })}
                      </span>
                    ) : null}
                    {inTicket ? (
                      <span style={menuQtyPill}>
                        {t('takeOrder.inCart', { count: inTicket.quantity })}
                      </span>
                    ) : null}
                  </button>
                );
              })}
              {visibleProducts.length === 0 ? (
                <div style={{ color: '#94a3b8' }}>{t('takeOrder.noMatch')}</div>
              ) : null}
            </div>
          </div>

          {/* Ticket.
              `position: sticky; top: 12px` now lives in .take-order-ticket rather
              than inline, because inline styles beat media queries and the mobile
              breakpoint has to unstick this panel. Background stays inline — it is
              driven by addMode. */}
          <div
            className="take-order-ticket"
            style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: addMode ? '#fffbeb' : '#f8fafc' }}
          >
            {/* ADD MODE BANNER (order-additions R4.2).
                Visually distinct — amber panel, amber border — because the worst
                outcome on this screen is a server believing they are building a NEW
                ticket while actually appending to one the kitchen already has. */}
            {addMode ? (
              <div
                style={{
                  border: '2px solid #f59e0b', background: '#fef3c7', borderRadius: 10,
                  padding: 10, marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, color: '#78350f', fontSize: '0.9rem' }}>
                      {t('takeOrder.add.bannerTitle', {
                        target: [
                          String(addMode.tableLabel || '').trim()
                            ? t('takeOrder.add.tableChip', { table: addMode.tableLabel })
                            : t('takeOrder.orderType.toGo'),
                          addMode.partyLabel,
                          addMode.customerName,
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      })}
                    </div>
                    <div style={{ color: '#92400e', fontSize: '0.78rem', marginTop: 2 }}>
                      {t(addMode.hasTicket ? 'takeOrder.add.bannerMetaTicket' : 'takeOrder.add.bannerMeta', {
                        orderNumber: addMode.displayNumber,
                        count: addMode.itemCount,
                        total: formatMinor(addMode.totalCents),
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={requestLeaveAddMode}
                    style={{
                      flexShrink: 0, padding: '6px 10px', borderRadius: 8,
                      border: '1px solid #d97706', background: '#fff', color: '#92400e',
                      fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer',
                    }}
                  >
                    {t('takeOrder.add.exit')}
                  </button>
                </div>
                {/* Collect Payment by QR (take-order-scan-to-pay). Lets the order taker
                    collect on an EXISTING open ticket they are updating, not just on the
                    send confirmation. Gated on the tenant opt-in (scanToPayEnabled) + a
                    hosted-checkout provider from general settings; the open-tickets strip
                    carries no payment/provider fields (a deliberate least-privilege
                    boundary), so the SERVER is the authority: _handle_scan_to_pay_create
                    returns 409 for an already-paid or non-Stripe order, and the modal
                    surfaces that. The order object carries only the identifiers the modal
                    needs. */}
                {(() => {
                  const scanOn = generalSettings?.scanToPayEnabled === true;
                  if (!scanOn) return null;
                  const provider = String(generalSettings?.posProvider || '').trim().toUpperCase();
                  if (!['STRIPE', 'SQUARE', 'CLOVER'].includes(provider)) return null;
                  const addModeOrder = {
                    order_number: addMode.orderNumber,
                    order_timestamp: addMode.orderTimestamp,
                    amount_total_cents: Number(addMode.totalCents) || 0,
                    currency: String(generalSettings?.currency || 'USD').toUpperCase(),
                    posProvider: provider,
                  };
                  // Disable while there are UNSENT line edits. The QR charges the order's
                  // STORED total; collecting before "Update Order" is pressed would bill the
                  // pre-edit amount and miss the item just added. So QR and Update Order act
                  // as a toggle: submit the update first (making the stored total correct),
                  // then collect.
                  const hasPendingEdits = lines.length > 0 || Object.keys(lineEdits).length > 0;
                  return (
                    <div>
                      <button
                        type="button"
                        onClick={() => { if (!hasPendingEdits) setScanToPayOrder(addModeOrder); }}
                        disabled={hasPendingEdits}
                        style={{
                          marginTop: 10, padding: '8px 12px', borderRadius: 8,
                          border: hasPendingEdits ? '1px solid #cbd5e1' : '1px solid #16a34a',
                          background: hasPendingEdits ? '#f1f5f9' : '#fff',
                          color: hasPendingEdits ? '#94a3b8' : '#166534',
                          fontWeight: 800, fontSize: '0.8rem',
                          cursor: hasPendingEdits ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Collect Payment by QR
                      </button>
                      {hasPendingEdits ? (
                        <div style={{ color: '#92400e', fontSize: '0.72rem', marginTop: 4 }}>
                          Update the order first so the QR charges the new total.
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {/* Order type, table, party and customer contact belong to the TARGET
                order in Add_Mode and are not being changed, so they are hidden
                rather than shown disabled (R4.5). */}
            {!addMode ? (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {/* Loop variable is `ot`, not `t`: `t` is the translator imported at
                  the top of this file, and shadowing it here would make every
                  t(...) call inside this block invoke a string. */}
              {['DINE_IN', 'TO_GO'].map((ot) => (
                <button
                  key={ot}
                  type="button"
                  onClick={() => setOrderType(ot)}
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: orderType === ot ? 'var(--admin-brand, #7c3aed)' : '#cbd5e1',
                    background: orderType === ot ? 'var(--admin-brand, #7c3aed)' : '#fff',
                    color: orderType === ot ? '#fff' : '#334155',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {ot === 'DINE_IN' ? t('takeOrder.orderType.dineIn') : t('takeOrder.orderType.toGo')}
                </button>
              ))}
            </div>
            ) : null}

            {/* MANDATORY FIELD INDICATORS. Each order type has exactly one required field
                (see REQUIRED_FIELD_BY_ORDER_TYPE), marked three ways so it cannot be missed
                on a busy screen: a red asterisk label, a red outline once lines exist and it
                is still blank, and the hint below the send button.

                The outline waits for `lines.length > 0` deliberately. An empty ticket that
                has only just been opened is not yet a mistake, and outlining a field in red
                the moment the screen loads trains people to ignore red. */}
            {addMode ? null : orderType === 'DINE_IN' ? (
              <div style={{ marginBottom: 12 }}>
                <label style={requiredLabel} htmlFor="take-order-table">
                  {t('takeOrder.tableLabel')}
                  <span style={requiredMark} aria-hidden="true"> *</span>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="take-order-table"
                    className="pos-input"
                    type="text"
                    required
                    aria-required="true"
                    aria-invalid={missingField === 'tableLabel' && lines.length > 0}
                    value={tableLabel}
                    onChange={(e) => setTableLabel(e.target.value.slice(0, 40))}
                    placeholder={t('takeOrder.tablePlaceholder.web')}
                    style={{
                      flex: 2,
                      ...(missingField === 'tableLabel' && lines.length > 0 ? requiredInputMissing : {}),
                    }}
                  />
                  <input
                    className="pos-input"
                    type="number"
                    min="1"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                    placeholder={t('takeOrder.guests')}
                    style={{ flex: 1 }}
                  />
                </div>

                {/* Table party subdivision (R3.1, R3.2, R4.2, R4.6, R9.1).
                    Rendered only for an opted-in tenant, so a tenant that never asked
                    for this sees the dine-in form exactly as it is today (R1.1). */}
                {partyControlVisible ? (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontWeight: 700, fontSize: 13, color: '#334155' }}>
                      {t('takeOrder.party.label')}
                    </label>

                    {/* Open-ticket awareness (R4.2). Shown BEFORE the chips because it
                        is the reason the pre-selected letter is what it is — a server
                        who sees "this table already has 1 open ticket" understands the
                        suggestion instead of second-guessing it. */}
                    {partyTicketsAtTable.length > 0 ? (
                      <div
                        style={{
                          margin: '6px 0',
                          padding: '8px 10px',
                          borderRadius: 8,
                          background: '#fffbeb',
                          border: '1px solid #fcd34d',
                          fontSize: 12,
                          color: '#92400e',
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {partyTicketsAtTable.length === 1
                            ? t('takeOrder.party.openTicketsOne')
                            : t('takeOrder.party.openTicketsMany', {
                                count: partyTicketsAtTable.length,
                              })}
                        </div>
                        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {partyTicketsAtTable.map((tk) => {
                            const label = String(tk.partyLabel || '').trim();
                            const total = formatMinor(Number(tk.amount_total_cents) || 0);
                            return (
                              <span
                                key={String(tk.order_number || label)}
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  background: '#fef3c7',
                                  border: '1px solid #fbbf24',
                                  fontWeight: 700,
                                }}
                              >
                                {label
                                  ? t('takeOrder.party.openTicketEntry', { label, total })
                                  : t('takeOrder.party.openTicketNoLabel', { total })}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0' }}>
                      {/* "No party" is offered because a party is never REQUIRED (R3.5).
                          A server at a table with one group must be able to say so
                          rather than being forced to pick a letter that means nothing. */}
                      <button
                        type="button"
                        onClick={() => choosePartyLabel('')}
                        aria-pressed={!partyLabel}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid',
                          borderColor: !partyLabel ? 'var(--admin-brand, #7c3aed)' : '#cbd5e1',
                          background: !partyLabel ? 'var(--admin-brand, #7c3aed)' : '#fff',
                          color: !partyLabel ? '#fff' : '#334155',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        {t('takeOrder.party.none')}
                      </button>
                      {PARTY_LABELS.map((code) => {
                        const active = partyLabel === code;
                        const inUse = partyLabelsInUse.includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => choosePartyLabel(code)}
                            aria-pressed={active}
                            // The identifier stays LATIN in every language (R9.2); only
                            // the surrounding label is translated.
                            aria-label={t('takeOrder.party.select', { label: code })}
                            style={{
                              minWidth: 40,
                              padding: '6px 10px',
                              borderRadius: 8,
                              border: '1px solid',
                              borderColor: active ? 'var(--admin-brand, #7c3aed)' : inUse ? '#fbbf24' : '#cbd5e1',
                              background: active ? 'var(--admin-brand, #7c3aed)' : inUse ? '#fffbeb' : '#fff',
                              color: active ? '#fff' : '#334155',
                              fontWeight: 800,
                              cursor: 'pointer',
                            }}
                          >
                            {code}
                          </button>
                        );
                      })}
                    </div>

                    {/* Non-blocking (R4.6). The lookup is a point-in-time read with no
                        lock, and the server may legitimately be adding a second ticket
                        for party A, so this informs rather than refuses. */}
                    {partyCollision ? (
                      <div style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>
                        {t('takeOrder.party.collision', { label: partyLabel })}
                      </div>
                    ) : null}

                    <input
                      className="pos-input"
                      type="text"
                      value={partyName}
                      onChange={(e) => setPartyName(e.target.value.slice(0, 40))}
                      placeholder={t('takeOrder.party.namePlaceholder')}
                      aria-label={t('takeOrder.party.nameLabel')}
                      style={{ marginTop: 6, width: '100%' }}
                    />
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                      {t('takeOrder.party.hint')}
                    </div>
                  </div>
                ) : null}

                {/* OPTIONAL customer name for a dine-in ticket. Dine-in is identified by
                    table (required above), so this is not required — but capturing a name
                    when the guest gives one makes the open-order list searchable by name as
                    well as table. Sent as `customerName` (already allow-listed in the submit
                    payload and kept for dine-in by the create handler). */}
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }} htmlFor="take-order-dinein-customer">
                    {t('takeOrder.customerNameLabel')}
                    <span style={{ color: '#94a3b8', fontWeight: 500 }}> (optional)</span>
                  </label>
                  <input
                    id="take-order-dinein-customer"
                    className="pos-input"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value.slice(0, 120))}
                    placeholder={t('takeOrder.customerName.web')}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <label style={requiredLabel} htmlFor="take-order-customer">
                  {t('takeOrder.customerNameLabel')}
                  <span style={requiredMark} aria-hidden="true"> *</span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    id="take-order-customer"
                    className="pos-input"
                    type="text"
                    required
                    aria-required="true"
                    aria-invalid={missingField === 'customerName' && lines.length > 0}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value.slice(0, 120))}
                    placeholder={t('takeOrder.customerName.web')}
                    style={missingField === 'customerName' && lines.length > 0 ? requiredInputMissing : undefined}
                  />
                  <input
                    className="pos-input"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.slice(0, 40))}
                    placeholder={t('takeOrder.customerPhone')}
                  />
                </div>
              </div>
            )}

            {/* Already on the target order — EDITABLE (order-line-removal follow-up).
                Was read-only under additions-only; the owner asked for waiter-decided
                quantity edits and removal with no extra permission or lock, so each
                line now carries its own +/- and remove, applying immediately rather
                than joining the pending "Adding" batch below. */}
            {addMode ? (
              <div
                style={{
                  borderTop: '1px solid #e2e8f0', paddingTop: 12, marginBottom: 4,
                }}
              >
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  {t('takeOrder.add.alreadyOnOrder')}
                </div>
                <div
                  style={{
                    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8,
                    padding: 10,
                  }}
                >
                  {addMode.existingLines.length > 0 ? (
                    <div>
                      {addMode.existingLines.map((l, idx) => {
                        const staged = Object.prototype.hasOwnProperty.call(lineEdits, idx);
                        const displayQty = staged ? lineEdits[idx] : l.quantity;
                        return (
                        <div
                          key={`${l.name}-${idx}`}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            gap: 8, padding: '6px 0',
                            borderBottom: idx < addMode.existingLines.length - 1 ? '1px dashed #cbd5e1' : 'none',
                            opacity: displayQty === 0 ? 0.5 : 1,
                          }}
                        >
                          <span
                            style={{
                              overflowWrap: 'anywhere', fontSize: '0.82rem',
                              color: displayQty === 0 ? '#94a3b8' : '#0f172a',
                              textDecoration: displayQty === 0 ? 'line-through' : 'none',
                            }}
                          >
                            {localizedName({ ProductName: l.name, ProductNameI18n: l.nameI18n }, activeLanguage)}
                            {staged ? (
                              <span style={{ color: '#d97706', fontWeight: 800, fontStyle: 'italic', textDecoration: 'none' }}>
                                {' '}{t('takeOrder.add.pendingChange')}
                              </span>
                            ) : null}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => editExistingLine(idx, displayQty - 1)}
                              style={existingLineQtyBtn}
                              aria-label={t('takeOrder.remove')}
                            >
                              −
                            </button>
                            <span style={{ minWidth: 18, textAlign: 'center', fontWeight: 800, fontSize: '0.82rem' }}>
                              {displayQty}
                            </span>
                            <button
                              type="button"
                              onClick={() => editExistingLine(idx, displayQty + 1)}
                              style={existingLineQtyBtn}
                            >
                              +
                            </button>
                            <span style={{ fontWeight: 700, fontSize: '0.82rem', minWidth: 60, textAlign: 'right' }}>
                              {formatMinor(l.unitPriceCents * displayQty)}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Fallback for a ticket fetched before this field existed (e.g. a
                    // cached response mid-deploy). Never blocks Add_Mode on it; simply
                    // not editable from this panel until refreshed.
                    <div style={{ color: '#64748b', fontSize: '0.82rem' }}>
                      {t('takeOrder.add.alreadyOnOrderSummary', {
                        count: addMode.itemCount,
                        total: formatMinor(addMode.totalCents),
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>
                  {addMode ? t('takeOrder.add.adding') : t('takeOrder.currentOrder')}
                </span>
                {lines.length ? (
                  <span style={cartCountBadge}>{lines.reduce((a, l) => a + l.quantity, 0)}</span>
                ) : null}
              </div>
              {/* Which party this ticket is being built for, visible at every moment
                  (R3.4). The point is that a server adding a dish looks here and not at
                  the form above: on a shared table the two tickets differ by one letter,
                  and the moment to notice a mistake is before "Send", not on the chit. */}
              {partyControlVisible && partyLabel.trim() ? (
                <div
                  style={{
                    marginBottom: 10,
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: 'var(--admin-brand-tint-soft, #f5f3ff)',
                    border: '1px solid var(--admin-brand-tint-border, #ddd6fe)',
                    color: 'var(--admin-brand-ink, #5b21b6)',
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {t('takeOrder.party.ticketHeader', {
                    table: tableLabel.trim() || '—',
                    label: partyLabel.trim(),
                  })}
                  {partyName.trim() ? ` · ${partyName.trim()}` : ''}
                </div>
              ) : null}
              {lines.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>🧾</div>
                  {addMode ? t('takeOrder.add.emptyBatch') : t('takeOrder.emptyTicket.web')}
                </div>
              ) : (
                lines.map((l) => {
                  const lid = l.lineId || l.productId;
                  return (
                  <div key={lid} style={cartLineCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>
                        {/* Rendered from the line's display-only translations; `l.name`
                            remains the English value that gets submitted (R9.1, R9.6). */}
                        {localizedName({ ProductName: l.name, ProductNameI18n: l.nameI18n }, activeLanguage)}
                      </span>
                      <span style={{ fontWeight: 800 }}>{formatMinor(l.unitPriceCents * l.quantity)}</span>
                    </div>
                    {/* The customization summary, which is what makes two lines of the same
                        product distinguishable on the ticket. English: it is the same string
                        that prints on the chit, and prints stay English by owner request. */}
                    {l.modifierSummary ? (
                      <div style={{ color: 'var(--admin-brand, #7c3aed)', fontSize: '0.78rem', fontWeight: 700, marginTop: 4, overflowWrap: 'anywhere' }}>
                        {l.modifierSummary}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <button type="button" onClick={() => onQty(lid, l.quantity - 1)} style={qtyBtn}>−</button>
                      <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 800, fontSize: '1rem' }}>{l.quantity}</span>
                      <button type="button" onClick={() => onQty(lid, l.quantity + 1)} style={qtyBtn}>+</button>
                      <button
                        type="button"
                        onClick={() => onQty(lid, 0)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#dc2626', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        {t('takeOrder.remove')}
                      </button>
                    </div>
                    <input
                      className="pos-input"
                      type="text"
                      value={l.itemNotes}
                      onChange={(e) => onNotes(lid, e.target.value)}
                      placeholder={t('takeOrder.itemNotes')}
                      style={{ width: '100%', marginTop: 8, fontSize: '0.85rem', background: '#fff' }}
                    />
                  </div>
                  );
                })
              )}
            </div>

            {/* In Add_Mode the meaningful numbers are what is being added and what the
                ORDER total becomes — the batch's own subtotal in isolation is not what
                anyone needs to know. */}
            {addMode && addPreview ? (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#475569' }}>{t('takeOrder.add.adding')}</span>
                  <span style={{ fontWeight: 700 }}>+{formatMinor(addPreview.addedSubtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '1.05rem' }}>
                  <span>{t('takeOrder.add.newOrderTotal')}</span>
                  <span>{formatMinor(addPreview.newTotal)}</span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4 }}>
                  {t('takeOrder.add.wasTotal', { total: formatMinor(addPreview.oldTotal) })}
                </div>
              </div>
            ) : (
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginTop: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#475569' }}>{t('takeOrder.subtotal')}</span>
                <span style={{ fontWeight: 700 }}>{formatMinor(sub)}</span>
              </div>
              {preview ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#475569' }}>{preview.label}</span>
                  <span style={{ fontWeight: 700 }}>{preview.inclusive ? '' : '+'}{formatMinor(preview.taxCents)}</span>
                </div>
              ) : null}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '1.05rem' }}>
                <span>{t('takeOrder.total')}</span>
                <span>{formatMinor(totalCents)}</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4 }}>
                {t('takeOrder.estimateNote.web')}
              </div>
            </div>
            )}

            {/* Order notes belong to the target order in Add_Mode and are not being
                changed, so the field is hidden there. */}
            {!addMode ? (
            <textarea
              className="pos-input"
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder={t('takeOrder.orderNotes')}
              rows={2}
              style={{ width: '100%', marginTop: 12 }}
            />
            ) : null}

            <button
              type="button"
              onClick={addMode ? () => setConfirmOpen(true) : () => setReviewOpen(true)}
              disabled={!submitEnabled}
              style={{
                width: '100%',
                marginTop: 12,
                padding: '14px 0',
                borderRadius: 10,
                border: 'none',
                background: submitEnabled ? (addMode ? '#d97706' : '#16a34a') : '#cbd5e1',
                color: '#fff',
                fontWeight: 900,
                fontSize: '1rem',
                cursor: submitEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting
                ? t('takeOrder.sending')
                : addMode
                  ? t('takeOrder.add.update')
                  : /* Opens the review modal; 'Send to kitchen' is now the label on the
                       modal's own submit button, which is the only thing that posts. */
                    t('takeOrder.review')}
            </button>
            {/* Says WHICH field, not "fill in the required fields". The button is disabled
                and the reason has to be findable without hunting. */}
            {missingField && lines.length > 0 ? (
              <div role="status" style={{ color: '#b45309', fontSize: '0.8rem', marginTop: 6 }}>
                {t(missingFieldPromptKey(submitState))}
              </div>
            ) : null}
            {addMode && canCancelOrder ? (
              <button
                type="button"
                onClick={cancelCurrentOrder}
                disabled={cancelling}
                style={{
                  width: '100%', marginTop: 8, padding: '10px 0', borderRadius: 8,
                  border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c',
                  fontWeight: 800, cursor: cancelling ? 'not-allowed' : 'pointer',
                  opacity: cancelling ? 0.6 : 1,
                }}
              >
                {cancelling ? t('takeOrder.add.cancelling') : t('takeOrder.add.cancelOrder')}
              </button>
            ) : null}
            {(lines.length > 0 || (addMode && Object.keys(lineEdits).length > 0)) ? (
              <button
                type="button"
                onClick={
                  addMode
                    ? () => {
                        setLines([]);
                        setLineEdits({});
                      }
                    : onClear
                }
                style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, cursor: 'pointer' }}
              >
                {addMode ? t('takeOrder.add.clearBatch') : t('takeOrder.clearTicket.web')}
              </button>
            ) : null}

            {/* Post-addition result, with the two follow-up actions in reach (R5.8/R5.9)
                so a server does not have to navigate to the queue to print. */}
            {lastResult ? (
              <div
                style={{
                  marginTop: 12, padding: 10, borderRadius: 8,
                  background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
                  fontSize: '0.82rem',
                }}
              >
                <div style={{ fontWeight: 800 }}>
                  {t('takeOrder.add.resultTotal', { total: formatMinor(lastResult.totalCents) })}
                </div>
                {lastResult.billStale ? (
                  <div style={{ color: '#92400e', marginTop: 4 }}>
                    {t('takeOrder.add.billStaleLong')}
                  </div>
                ) : null}
                <div style={{ marginTop: 6, color: '#475569' }}>
                  {t('takeOrder.add.printHint')}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* CUSTOMIZATION. Shown only for a product that HAS modifier groups (R2.2), so the
          one-tap add is untouched for everything else. */}
      {customizing ? (
        <CustomizeSheet
          state={customizing}
          showErrors={showCustomizeErrors}
          activeLanguage={activeLanguage}
          onToggle={onToggleOption}
          onCancel={closeCustomize}
          onConfirm={confirmCustomize}
        />
      ) : null}

      <ScrollToEnds />

      {/* CONFIRMATION. Never commit a change without showing exactly what is being
          added AND what is being adjusted/removed, and what the order total becomes.
          This is the last point at which a mistake is free to correct. */}
      {confirmOpen && addMode && addPreview ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('takeOrder.add.confirmTitle')}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setConfirmOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 20, width: 'min(460px, 100%)',
              boxShadow: '0 20px 50px rgba(15,23,42,0.35)',
            }}
          >
            <h2 style={{ margin: '0 0 4px', fontSize: '1.05rem', color: '#0f172a' }}>
              {t('takeOrder.add.confirmTitle')}
            </h2>
            <div style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 12 }}>
              {t(addMode.hasTicket ? 'takeOrder.add.bannerMetaTicket' : 'takeOrder.add.bannerMeta', {
                orderNumber: addMode.displayNumber,
                count: addMode.itemCount,
                total: formatMinor(addMode.totalCents),
              })}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
              {Object.entries(lineEdits).map(([idxStr, qty]) => {
                const l = addMode.existingLines[Number(idxStr)];
                if (!l) return null;
                const removed = qty === 0;
                return (
                  <div
                    key={`edit-${idxStr}`}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}
                  >
                    <span style={{ color: removed ? '#b91c1c' : '#0f172a', overflowWrap: 'anywhere' }}>
                      {removed
                        ? t('takeOrder.add.removingLine')
                        : t('takeOrder.add.changingLineQty', { quantity: qty })}
                      {' '}
                      {localizedName({ ProductName: l.name, ProductNameI18n: l.nameI18n }, activeLanguage)}
                    </span>
                    <span style={{ fontWeight: 700, flexShrink: 0 }}>
                      {formatMinor(l.unitPriceCents * qty)}
                    </span>
                  </div>
                );
              })}
              {lines.map((l) => (
                <div
                  key={l.lineId || l.productId}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}
                >
                  <span style={{ color: '#0f172a', overflowWrap: 'anywhere' }}>
                    + {l.quantity} ×{' '}
                    {localizedName({ ProductName: l.name, ProductNameI18n: l.nameI18n }, activeLanguage)}
                    {/* Two lines of the same dish differ ONLY by this, so the confirmation
                        is unreadable without it. */}
                    {l.modifierSummary ? (
                      <span style={{ color: 'var(--admin-brand, #7c3aed)', fontWeight: 700 }}>{` — ${l.modifierSummary}`}</span>
                    ) : null}
                  </span>
                  <span style={{ fontWeight: 700, flexShrink: 0 }}>
                    {formatMinor(l.unitPriceCents * l.quantity)}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', marginBottom: 4 }}>
                <span>{t('takeOrder.add.wasTotalLabel')}</span>
                <span>{formatMinor(addPreview.oldTotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '1.05rem', color: '#0f172a' }}>
                <span>{t('takeOrder.add.newOrderTotal')}</span>
                <span>{formatMinor(addPreview.newTotal)}</span>
              </div>
            </div>

            {willInvalidatePrintedBill(addMode) ? (
              <div
                style={{
                  marginTop: 10, padding: 8, borderRadius: 8, background: '#fef3c7',
                  border: '1px solid #f59e0b', color: '#92400e', fontSize: '0.8rem',
                }}
              >
                {t('takeOrder.add.billWillBeStale')}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('takeOrder.add.cancel')}
              </button>
              <button
                type="button"
                onClick={updateOrder}
                disabled={submitting}
                style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: submitting ? '#cbd5e1' : '#16a34a', color: '#fff', fontWeight: 900, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? t('takeOrder.sending') : t('takeOrder.add.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* REVIEW ORDER (new tickets only). The last look at the ticket before it reaches
          the kitchen, and on a phone the FIRST look for a server who never scrolled down
          to the ticket panel.

          Order details are read-only here on purpose: this screen answers "is this right?",
          and a modal that also let you retype the table number would just be the form
          again. Quantities are the one thing editable, because "one too many" is the
          mistake this modal exists to catch. To change anything else, Back to menu.

          `lines.length > 0` is a second latch behind the explicit close in sendToKitchen:
          a sent ticket empties `lines`, so the modal can never linger over an empty one. */}
      {reviewOpen && !addMode && lines.length > 0 ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('takeOrder.review.title')}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: 16,
            // If the card cannot fit (a short laptop window, or a long details block),
            // the BACKDROP scrolls. Without this the footer buttons could sit off-screen
            // with no way to reach them.
            overflowY: 'auto',
          }}
          onClick={() => setReviewOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, width: 'min(560px, 100%)',
              boxShadow: '0 20px 50px rgba(15,23,42,0.35)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ padding: '20px 20px 0' }}>
              <h2 style={{ margin: '0 0 12px', fontSize: '1.05rem', color: '#0f172a' }}>
                {t('takeOrder.review.title')}
              </h2>

              {/* Locked order details. */}
              <div
                style={{
                  padding: 10, borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: '0.78rem', color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {t('takeOrder.review.orderDetails')}
                </div>
                {[
                  {
                    k: 'type',
                    label: t('takeOrder.review.orderTypeLabel'),
                    value: orderType === 'DINE_IN' ? t('takeOrder.orderType.dineIn') : t('takeOrder.orderType.toGo'),
                  },
                  ...(orderType === 'DINE_IN' && tableLabel.trim()
                    ? [{ k: 'table', label: t('takeOrder.tableLabel'), value: tableLabel.trim() }]
                    : []),
                  // Party only where the tenant opted in, matching the payload rule in
                  // sendToKitchen — reviewing a field that will not be sent is a lie.
                  ...(partyControlVisible && partyLabel.trim()
                    ? [{
                        k: 'party',
                        label: t('takeOrder.party.label'),
                        value: partyLabel.trim() + (partyName.trim() ? ` · ${partyName.trim()}` : ''),
                      }]
                    : []),
                  ...(String(guestCount || '').trim()
                    ? [{ k: 'guests', label: t('takeOrder.guests'), value: String(guestCount).trim() }]
                    : []),
                  ...(customerName.trim()
                    ? [{ k: 'cname', label: t('takeOrder.customerNameLabel'), value: customerName.trim() }]
                    : []),
                  ...(customerPhone.trim()
                    ? [{ k: 'cphone', label: t('takeOrder.customerPhoneLabel'), value: customerPhone.trim() }]
                    : []),
                  ...(orderNotes.trim()
                    ? [{ k: 'notes', label: t('takeOrder.review.notesLabel'), value: orderNotes.trim() }]
                    : []),
                ].map((row) => (
                  <div
                    key={row.k}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4, fontSize: '0.86rem' }}
                  >
                    <span style={{ color: '#64748b', flexShrink: 0 }}>{row.label}</span>
                    <span style={{ color: '#0f172a', fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ fontWeight: 800, fontSize: '0.78rem', color: '#64748b', margin: '14px 0 6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                {t('takeOrder.review.items')}
                <span style={{ fontWeight: 700, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}>
                  {` · ${t('takeOrder.inCart', { count: lines.reduce((a, l) => a + l.quantity, 0) })}`}
                </span>
              </div>
            </div>

            {/* Scrollable item list.
                A plain row is ~83px (name+price, quantity controls, padding); one carrying
                both a customization summary and an item note runs to ~121px. The 300px
                floor therefore keeps three plain rows — the requirement — and a little over
                two of the tallest kind, with the rest a scroll away. It is applied only once
                there are 3 lines to show, so a one-item ticket does not open with a panel of
                dead space. Where the floor cannot fit the window, the BACKDROP scrolls
                (above), so the footer is still reachable. */}
            <div
              style={{
                overflowY: 'auto',
                margin: '0 20px',
                borderTop: '1px solid #e2e8f0',
                maxHeight: 'min(55vh, 520px)',
                minHeight: lines.length >= 3 ? 300 : undefined,
              }}
            >
              {lines.map((l) => {
                const lid = l.lineId || l.productId;
                return (
                  <div key={`rv-${lid}`} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>
                        {localizedName({ ProductName: l.name, ProductNameI18n: l.nameI18n }, activeLanguage)}
                      </span>
                      <span style={{ fontWeight: 800, flexShrink: 0 }}>
                        {formatMinor(l.unitPriceCents * l.quantity)}
                      </span>
                    </div>
                    {/* Read-only here. Two lines of the same dish differ only by this, so
                        without it the list is ambiguous — but editing it would mean
                        rebuilding the modifier picker inside the modal. */}
                    {l.modifierSummary ? (
                      <div style={{ color: 'var(--admin-brand, #7c3aed)', fontSize: '0.78rem', fontWeight: 700, marginTop: 2, overflowWrap: 'anywhere' }}>
                        {l.modifierSummary}
                      </div>
                    ) : null}
                    {l.itemNotes && l.itemNotes.trim() ? (
                      <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 2, overflowWrap: 'anywhere' }}>
                        {l.itemNotes.trim()}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      {/* Stops at 1. Decrementing to zero is how the inline panel removes a
                          line, but there a mis-tap is next to the whole ticket; here it
                          would silently drop a line from the very list being checked. */}
                      <button
                        type="button"
                        onClick={() => onQty(lid, Math.max(1, l.quantity - 1))}
                        disabled={l.quantity <= 1}
                        aria-label="−"
                        style={{ ...qtyBtn, opacity: l.quantity <= 1 ? 0.4 : 1, cursor: l.quantity <= 1 ? 'not-allowed' : 'pointer' }}
                      >
                        −
                      </button>
                      <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 800, fontSize: '1rem' }}>
                        {l.quantity}
                      </span>
                      <button type="button" onClick={() => onQty(lid, l.quantity + 1)} aria-label="+" style={qtyBtn}>+</button>
                      <button
                        type="button"
                        onClick={() => onQty(lid, 0)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#dc2626', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        {t('takeOrder.remove')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '12px 20px 20px' }}>
              {/* Same memos as the inline panel (`sub`/`preview`/`totalCents`) rather than
                  a second computation, so the two can never disagree. */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#475569' }}>{t('takeOrder.subtotal')}</span>
                  <span style={{ fontWeight: 700 }}>{formatMinor(sub)}</span>
                </div>
                {preview ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#475569' }}>{preview.label}</span>
                    <span style={{ fontWeight: 700 }}>{preview.inclusive ? '' : '+'}{formatMinor(preview.taxCents)}</span>
                  </div>
                ) : null}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '1.05rem' }}>
                  <span>{t('takeOrder.total')}</span>
                  <span>{formatMinor(totalCents)}</span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4 }}>
                  {t('takeOrder.estimateNote.web')}
                </div>
              </div>

              {/* A failed send leaves this modal open, so the error has to be readable
                  here — the page-level banner is behind the backdrop. */}
              {error ? (
                <div
                  role="alert"
                  style={{
                    marginTop: 10, padding: 8, borderRadius: 8, background: '#fef2f2',
                    border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '0.82rem',
                  }}
                >
                  {error}
                </div>
              ) : null}

              {missingField ? (
                <div role="status" style={{ color: '#b45309', fontSize: '0.8rem', marginTop: 8 }}>
                  {t(missingFieldPromptKey(submitState))}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                {/* Closes and keeps the ticket. Named for where it goes rather than
                    "Cancel", which next to a built-up order reads as "discard it". */}
                <button
                  type="button"
                  onClick={() => setReviewOpen(false)}
                  style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 800, cursor: 'pointer' }}
                >
                  {t('takeOrder.review.backToMenu')}
                </button>
                <button
                  type="button"
                  onClick={sendToKitchen}
                  disabled={!submitEnabled}
                  style={{ flex: 2, padding: '12px 0', borderRadius: 8, border: 'none', background: submitEnabled ? '#16a34a' : '#cbd5e1', color: '#fff', fontWeight: 900, fontSize: '1rem', cursor: submitEnabled ? 'pointer' : 'not-allowed' }}
                >
                  {submitting ? t('takeOrder.sending') : t('takeOrder.send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Whether appending will leave a printed guest bill out of date (R5.6).
 *
 * True whenever a bill has been printed AT ALL, not merely when it is already stale:
 * any addition invalidates a printed bill, so the warning has to fire on the first
 * addition after printing — which is exactly the case where nothing is stale yet.
 *
 * When no bill was ever printed there is nothing to invalidate and no warning, which
 * keeps the common path clean.
 */
export function willInvalidatePrintedBill(addMode) {
  if (!addMode) return false;
  const printed = Number(addMode.billPrintedAt);
  return Number.isFinite(printed) && printed > 0;
}

const existingLineQtyBtn = {
  width: 24,
  height: 24,
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#475569',
  fontWeight: 800,
  cursor: 'pointer',
  fontSize: '0.82rem',
  lineHeight: 1,
};

const qtyBtn = {
  border: '1px solid var(--admin-brand-tint-border, #e2d9fb)',
  background: '#fff',
  borderRadius: 999,
  width: 34,
  height: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontWeight: 800,
  fontSize: '1.05rem',
  color: 'var(--admin-brand, #7c3aed)',
  lineHeight: 1,
};

const menuCard = {
  textAlign: 'left',
  border: '1px solid #eef2f7',
  borderRadius: 14,
  padding: 14,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minHeight: 92,
  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
  position: 'relative',
};

const menuPlus = {
  width: 26,
  height: 26,
  borderRadius: 999,
  background: 'var(--admin-brand, #7c3aed)',
  color: 'var(--admin-brand-text, #fff)',
  fontWeight: 900,
  fontSize: '1.1rem',
  lineHeight: '24px',
  textAlign: 'center',
};

const menuQtyPill = {
  alignSelf: 'flex-start',
  background: 'var(--admin-brand, #7c3aed)',
  color: 'var(--admin-brand-text, #fff)',
  fontWeight: 800,
  fontSize: '0.7rem',
  borderRadius: 999,
  padding: '2px 8px',
};

const cartCountBadge = {
  marginLeft: 8,
  minWidth: 24,
  height: 24,
  borderRadius: 999,
  background: 'var(--admin-brand, #7c3aed)',
  color: 'var(--admin-brand-text, #fff)',
  fontWeight: 900,
  fontSize: '0.8rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
};

const cartLineCard = {
  background: '#fff',
  border: '1px solid #eef2f7',
  borderRadius: 12,
  padding: 12,
  marginBottom: 10,
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.05)',
};

// --- Mandatory field indicators ---------------------------------------------
// A visible label, not placeholder text alone: a placeholder disappears the moment someone
// types, so a field that looked required while empty stops looking required exactly when
// they might want to check it.
const requiredLabel = {
  display: 'block',
  fontSize: '0.76rem',
  fontWeight: 800,
  color: '#475569',
  marginBottom: 4,
};

const requiredMark = { color: '#dc2626' };

// The asterisk carries the requirement; this is the "and you have not filled it in yet"
// state, applied only once the ticket has lines.
const requiredInputMissing = {
  borderColor: '#dc2626',
  boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.15)',
};

// --- Jump-to-top / jump-to-bottom -------------------------------------------
// Deliberately large: this is a target for a thumb on a tablet propped on a counter, not a
// mouse pointer. 44px is the usual floor for a touch target and this sits above it.
const scrollJumpBtn = {
  width: 44,
  height: 44,
  borderRadius: 999,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: 'var(--admin-brand, #7c3aed)',
  fontSize: '1.15rem',
  fontWeight: 900,
  lineHeight: 1,
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.18)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function chipStyle(active) {
  return {
    border: '1px solid',
    borderColor: active ? 'var(--admin-brand, #7c3aed)' : '#cbd5e1',
    background: active ? 'var(--admin-brand, #7c3aed)' : '#fff',
    color: active ? 'var(--admin-brand-text, #fff)' : '#334155',
    borderRadius: 999,
    padding: '6px 14px',
    fontWeight: 700,
    cursor: 'pointer',
  };
}
